import type { CustomLandingLang } from "./customLanding";

export type CustomCaseViewerLang = CustomLandingLang;

const COLD_CASE_WEBSITE_URL = "https://app.coldcasegamekits.com/agency";

const SUPPORTED_CUSTOM_CASE_VIEWER_LANGS = new Set<CustomCaseViewerLang>([
  "en",
  "fr",
  "es",
  "de",
  "it",
]);

const CUSTOM_CASE_VIEWER_LOCALE_TAGS: Record<CustomCaseViewerLang, string> = {
  en: "en-US",
  fr: "fr-FR",
  es: "es-ES",
  de: "de-DE",
  it: "it-IT",
};

const CUSTOM_CASE_VIEWER_LANG_ALIASES: Record<string, CustomCaseViewerLang> = {
  english: "en",
  anglais: "en",
  ingles: "en",
  inglese: "en",
  french: "fr",
  francais: "fr",
  "français": "fr",
  spanish: "es",
  espanol: "es",
  "español": "es",
  german: "de",
  deutsch: "de",
  italian: "it",
  italiano: "it",
};

export const normalizeCustomCaseViewerLang = (
  value: unknown,
): CustomCaseViewerLang | "" => {
  const normalized = String(value || "").trim().toLowerCase().replace(/_/g, "-");
  if (!normalized) return "";

  const base = normalized.split("-")[0];
  if (SUPPORTED_CUSTOM_CASE_VIEWER_LANGS.has(base as CustomCaseViewerLang)) {
    return base as CustomCaseViewerLang;
  }

  return CUSTOM_CASE_VIEWER_LANG_ALIASES[base] || "";
};

export const resolveCustomCaseViewerLang = ({
  search = "",
  caseAccessLanguage = "",
  casePayloadLanguage = "",
}: {
  search?: string;
  caseAccessLanguage?: unknown;
  casePayloadLanguage?: unknown;
}) => {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const queryLangRaw = params.get("lang") || params.get("language") || "";
  const queryLang = normalizeCustomCaseViewerLang(queryLangRaw);
  if (queryLang) {
    return {
      lang: queryLang,
      source: "query_lang",
      rawValue: queryLangRaw,
      hasExplicitQuery: true,
      usedFallback: false,
    } as const;
  }

  const accessLang = normalizeCustomCaseViewerLang(caseAccessLanguage);
  if (accessLang) {
    return {
      lang: accessLang,
      source: "case_access_language",
      rawValue: String(caseAccessLanguage || ""),
      hasExplicitQuery: false,
      usedFallback: false,
    } as const;
  }

  const payloadLang = normalizeCustomCaseViewerLang(casePayloadLanguage);
  if (payloadLang) {
    return {
      lang: payloadLang,
      source: "case_payload_language",
      rawValue: String(casePayloadLanguage || ""),
      hasExplicitQuery: false,
      usedFallback: false,
    } as const;
  }

  return {
    lang: "en",
    source: queryLangRaw ? "unsupported_query_fallback" : "default_en",
    rawValue: queryLangRaw || "",
    hasExplicitQuery: Boolean(queryLangRaw),
    usedFallback: true,
  } as const;
};

export const buildCustomCaseViewerSearch = ({
  lang,
  force = false,
}: {
  lang: CustomCaseViewerLang;
  force?: boolean;
}) => {
  if (!force && lang === "en") return "";
  return `?lang=${encodeURIComponent(lang)}`;
};

export const formatCustomCaseViewerDateTime = (
  value: string | null | undefined,
  lang: CustomCaseViewerLang,
  fallback: string,
) => {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;

  try {
    return parsed.toLocaleString(CUSTOM_CASE_VIEWER_LOCALE_TAGS[lang], {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return parsed.toLocaleString();
  }
};

const CUSTOM_CASE_VIEWER_EVIDENCE_TYPE_LABELS: Record<
  string,
  Record<CustomCaseViewerLang, string>
> = {
  evidence: {
    en: "Evidence",
    fr: "Preuve",
    es: "Prueba",
    de: "Beweis",
    it: "Prova",
  },
  case_background: {
    en: "Case Background",
    fr: "Contexte de l'affaire",
    es: "Contexto del caso",
    de: "Fallhintergrund",
    it: "Contesto del caso",
  },
  crime_scene_photos: {
    en: "Crime Scene Photos",
    fr: "Photos de la scene",
    es: "Fotos de la escena",
    de: "Tatortfotos",
    it: "Foto della scena",
  },
  victim_profile: {
    en: "Victim Profile",
    fr: "Profil de la victime",
    es: "Perfil de la victima",
    de: "Opferprofil",
    it: "Profilo della vittima",
  },
  witness_statements: {
    en: "Witness Statements",
    fr: "Declarations des temoins",
    es: "Declaraciones de testigos",
    de: "Zeugenaussagen",
    it: "Dichiarazioni dei testimoni",
  },
  police_report: {
    en: "Police Report",
    fr: "Rapport de police",
    es: "Informe policial",
    de: "Polizeibericht",
    it: "Rapporto di polizia",
  },
  autopsy_report: {
    en: "Autopsy Report",
    fr: "Rapport d'autopsie",
    es: "Informe de autopsia",
    de: "Autopsiebericht",
    it: "Rapporto di autopsia",
  },
  suspect_emails: {
    en: "Suspect Emails",
    fr: "Emails des suspects",
    es: "Correos de sospechosos",
    de: "E-Mails der Verdachtigen",
    it: "Email dei sospetti",
  },
  suspect_texts: {
    en: "Suspect Texts",
    fr: "Messages des suspects",
    es: "Mensajes de sospechosos",
    de: "Nachrichten der Verdachtigen",
    it: "Messaggi dei sospetti",
  },
  additional_evidence: {
    en: "Additional Evidence",
    fr: "Preuves supplementaires",
    es: "Pruebas adicionales",
    de: "Weitere Beweise",
    it: "Prove aggiuntive",
  },
  timeline: {
    en: "Timeline",
    fr: "Chronologie",
    es: "Cronologia",
    de: "Zeitleiste",
    it: "Cronologia",
  },
  newspaper_article: {
    en: "Newspaper Article",
    fr: "Article de journal",
    es: "Articulo de periodico",
    de: "Zeitungsartikel",
    it: "Articolo di giornale",
  },
  newspaper_articles: {
    en: "Newspaper Article",
    fr: "Article de journal",
    es: "Articulo de periodico",
    de: "Zeitungsartikel",
    it: "Articolo di giornale",
  },
  solution: {
    en: "Solution",
    fr: "Resolution",
    es: "Solucion",
    de: "Losung",
    it: "Soluzione",
  },
};

const humanizeFallbackEvidenceType = (value: string) =>
  String(value || "evidence")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim() || "Evidence";

export const getLocalizedCustomCaseEvidenceTypeLabel = (
  value: string,
  lang: CustomCaseViewerLang,
) =>
  CUSTOM_CASE_VIEWER_EVIDENCE_TYPE_LABELS[String(value || "").trim().toLowerCase()]?.[lang] ||
  humanizeFallbackEvidenceType(value);

type CustomCaseViewerCopy = {
  pageTitleSuffix: string;
  brandName: string;
  customCaseFile: string;
  defaultTitle: string;
  defaultSummary: string;
  loadingMessage: string;
  accessProblem: string;
  evidenceFileNotFound: string;
  buildStatus: string;
  refresh: string;
  privateCaseTitle: string;
  privateCaseDescription: string;
  privateCaseBadge: string;
  statusSummary: Record<string, string>;
  createPdf: string;
  openCasePacket: string;
  copyPrivateLink: string;
  linkCopied: string;
  privateCustomDelivery: string;
  sharedCustomDelivery: string;
  accessMode: string;
  ownerLink: string;
  guestLink: string;
  privateCustomAccessChip: string;
  guestShareAccessChip: string;
  evidenceVolume: string;
  sections: (count: number) => string;
  evidenceRecords: (count: number) => string;
  imageAssets: (count: number) => string;
  caseState: string;
  suspects: string;
  evidenceFiles: string;
  caseHub: string;
  caseHubBody: string;
  detectiveBriefing: string;
  whatOpensHere: string;
  fullCaseExperience: string;
  fullCaseExperienceBody: string;
  privateLinkCard: string;
  privateLinkCardBody: string;
  customCaseEvidence: string;
  backToCaseFile: string;
  openPdfPacket: string;
  metadata: {
    date: string;
    source: string;
    confidentiality: string;
    evidenceType: string;
    noDate: string;
    noSource: string;
    internal: string;
  };
  pager: {
    previousFile: string;
    nextFile: string;
    previousEvidence: string;
    nextEvidence: string;
  };
  warrant: {
    detectiveLabel: string;
    detectiveRank: string;
    approved: string;
    rejected: string;
    reset: string;
  };
  share: {
    sectionEyebrow: string;
    sectionTitle: string;
    sectionBody: string;
    expiresIn24Hours: string;
    createIdle: string;
    createBusy: string;
    newestLink: string;
    copyLink: string;
    copied: string;
    created: string;
    revoked: string;
    revoke: string;
    noActiveLinks: string;
    guestLinkFallback: string;
    expiresPrefix: string;
    expiresSoon: string;
    accessCount: (count: number) => string;
  };
  errors: {
    loadCase: string;
    createGuestLink: string;
    copyGuestLink: string;
    revokeGuestLink: string;
    submitWarrant: string;
    downloadPdf: string;
    resetWarrant: string;
  };
  video: {
    eyebrow: string;
    title: string;
    open: string;
    empty: string;
    iframeTitle: string;
  };
  briefing: {
    eyebrow: string;
    title: string;
    buildStatusLink: string;
    defaultBody: string;
    setting: string;
    unknownPlace: string;
    unknownTime: string;
    delivery: string;
    deliveryValue: string;
    deliveryBody: string;
  };
  archive: {
    eyebrow: string;
    title: string;
    accessLabel: string;
    openFile: string;
    includedSuspectDossiers: string;
  };
  suspectDossiers: {
    eyebrow: string;
    title: string;
    count: (count: number) => string;
    fallbackName: (index: number) => string;
  };
  ownerGate: {
    title: string;
    description: string;
    copy: {
      loading: string;
      badge: string;
      sendCodeToOrderEmail: string;
      rememberedNotice: string;
      requestIdle: string;
      requestBusy: string;
      codeLabel: string;
      codePlaceholder: string;
      verifyIdle: string;
      verifyBusy: string;
      clearRememberedAccess: string;
      devCodePreview: string;
      invalidCode: string;
      statusCodeSent: string;
      statusVerified: string;
      statusCleared: string;
      loadAccessStateError: string;
      requestCodeError: string;
      verifyCodeError: string;
      clearAccessError: string;
    };
  };
  printModal: {
    eyebrow: string;
    title: string;
    description: string;
    close: string;
    theme: string;
    themeLight: string;
    themeLightDescription: string;
    themeDark: string;
    themeDarkDescription: string;
    evidenceSelection: string;
    allEvidenceIncluded: string;
    selectedEvidence: (count: number) => string;
    deselectAll: string;
    selectAll: string;
    fileLabel: (index: number) => string;
    footer: string;
    downloadIdle: string;
    downloadBusy: string;
  };
  media: {
    close: string;
    imageAlt: (index: number) => string;
    openLarge: string;
    signedPreviewUnavailable: string;
  };
  panels: {
    localizedEvidenceLabels: Record<string, string>;
    photoPacket: string;
    crimeScenePhotos: string;
    imageCount: (count: number) => string;
    noCrimeScenePhotos: string;
    witnessStatements: string;
    associatedWitness: string;
    personOfInterest: string;
    statementCount: (count: number) => string;
    noAssociatedPortrait: string;
    additionalStatements: string;
    victimProfile: string;
    identitySummary: string;
    dossier: string;
    victimPortrait: string;
    portraitPendingVictim: string;
    noStructuredProfileFields: string;
    noVictimNarrative: string;
    victimProfiles: string;
    suspectDossiers: string;
    suspectCount: (count: number) => string;
    openDossier: string;
    closeDossier: string;
    portraitPendingSuspect: string;
    suspectEvidence: string;
    noSuspectEvidence: string;
    witnessMentions: string;
    witnessMentionCount: (count: number) => string;
    noSuspects: string;
    noRoleProvided: string;
    motive: string;
    age: string;
    sex: string;
    hair: string;
    eyes: string;
    height: string;
    build: string;
    maritalStatus: string;
    occupation: string;
    employer: string;
    alibi: string;
    evidenceAgainst: string;
    evidenceInFavor: string;
    opportunity: string;
    behaviorNotes: string;
    noTextChainsAvailable: string;
    recoveredTextChain: (index: number) => string;
    messageChains: string;
    messageCount: (count: number) => string;
    chainLabel: (index: number) => string;
    recoveredEmail: (index: number) => string;
    subjectLine: string;
    universityMailArchive: string;
    emailThread: string;
    from: string;
    to: string;
    cc: string;
    date: string;
    recoveredFromArchiveExport: string;
    archivedPress: string;
    newspaperCoverage: string;
    pressClip: string;
    byline: string;
    staffReporter: string;
    undatedClipping: string;
    archiveReproduction: string;
    ottawaPoliceService: string;
    majorCrimesDivision: string;
    incidentReport: string;
    filedDocumentSummary: string;
    internalUse: string;
    fileLabel: (caseNumber: string) => string;
    caseNumber: string;
    pendingAssignment: string;
    incidentWindow: string;
    seeReportBody: string;
    bodyDiscovery: string;
    officeChiefForensicPathologist: string;
    forensicPathologyUnit: string;
    postMortemExaminationReport: string;
    autopsySummary: string;
    medicalExaminerCopy: string;
    caseLabel: (caseNumber: string) => string;
    decedent: string;
    notListed: string;
    caseFile: string;
    pending: string;
    sectionLabel: (index: number) => string;
    imagePrompt: string;
    portraitPrompt: string;
  };
  solution: {
    finalRevealEyebrow: string;
    readyTitle: string;
    readyDescription: string;
    warningEyebrow: string;
    warningDescription: string;
    arrestWarrant: string;
    greetingCustom: (actorLabel: string) => string;
    greetingMember: (rankLabel: string, actorLabel: string) => string;
    arrestPrompt: string;
    suspectSelection: string;
    selectedSuspect: string;
    noPhoto: string;
    suspectDossier: string;
    submitting: string;
    warrantApproved: string;
    warrantRequest: string;
    warrantFiled: string;
    previewOnly: string;
    previewOnlyDescription: string;
    attempts: string;
    filedSuspect: string;
    unknownSuspect: string;
    result: string;
    correctWarrantApproved: string;
    incorrectSuspectTryAgain: string;
    correctFirstWarrant: string;
    solutionUnlockedWithoutSolveCredit: string;
    recordSummary: (count: number, nextRankLabel?: string, solvesToNextRank?: number) => string;
    customSolutionUnlocked: string;
    customRetryPrompt: string;
    guestSessionUnlocked: string;
    solutionAccess: string;
    openOfficialSolution: string;
    reveal: string;
    hide: string;
    submitCorrectPrompt: string;
    submitWarrantPrompt: string;
    shareTheWin: string;
    shareDescription: string;
    shareNow: string;
    copied: string;
    copyFailed: string;
    copyShareText: string;
    shareTextCustom: (caseTitle: string) => string;
    shareTextAgency: (caseTitle: string) => string;
    nativeShareTitle: (caseTitle: string) => string;
  };
};

const buildCountLabel =
  (
    singular: string,
    plural: string,
    formatter?: (count: number) => string,
  ) =>
  (count: number) =>
    formatter ? formatter(count) : `${count} ${count === 1 ? singular : plural}`;

const CUSTOM_CASE_EVIDENCE_LABELS: Record<CustomCaseViewerLang, Record<string, string>> = {
  en: {},
  fr: {
    "case overview": "Apercu de l'affaire",
    "victim context": "Contexte de la victime",
    "key relationships": "Relations principales",
    "known tensions": "Tensions connues",
    "timeline pressure": "Pression chronologique",
    "why the case matters now": "Pourquoi l'affaire compte maintenant",
    "time stamped surveillance gap": "Decalage d'horodatage video",
    "personal habit evidence": "Indice d'habitude personnelle",
    "misleading financial trail": "Fausse piste financiere",
    "digital metadata": "Metadonnees numeriques",
    "physical environmental clue": "Indice environnemental physique",
    "emotional evidence": "Element emotionnel",
    "social relationship web evidence": "Reseau relationnel",
    "contradictory witness statements": "Declarations contradictoires",
    "object placement anomalies": "Anomalies de mise en scene",
    "old case file inconsistency": "Incoherence de l'ancien dossier",
    "incident summary": "Resume de l'incident",
    "responding officer": "Agent responsable",
    "persons involved": "Personnes impliquees",
    "scene observations": "Observations sur la scene",
    "actions taken": "Actions entreprises",
    summary: "Resume",
    victim: "Victime",
    "lead officer": "Agent principal",
    "badge #": "Numero d'insigne",
    "initial call time": "Heure de l'appel initial",
    "initial assessment": "Evaluation initiale",
    "primary scene": "Scene principale",
    "access notes": "Notes d'acces",
    "primary suspects": "Suspects principaux",
    "autopsy details": "Details de l'autopsie",
    examiner: "Medecin legiste",
    "estimated time of death": "Heure estimee du deces",
    "cause of death": "Cause du deces",
    "manner of death": "Nature du deces",
    height: "Taille",
    weight: "Poids",
    "identifying marks": "Signes distinctifs",
    "external findings": "Constatations externes",
    "internal findings": "Constatations internes",
    toxicology: "Toxicologie",
    "evidence recovered": "Elements recueillis",
    "examiner opinion": "Conclusion du medecin legiste",
  },
  es: {
    "case overview": "Resumen del caso",
    "victim context": "Contexto de la victima",
    "key relationships": "Relaciones principales",
    "known tensions": "Tensiones conocidas",
    "timeline pressure": "Presion cronologica",
    "why the case matters now": "Por que importa el caso ahora",
    "time stamped surveillance gap": "Brecha de vigilancia con marca horaria",
    "personal habit evidence": "Prueba de habito personal",
    "misleading financial trail": "Pista financiera enganosa",
    "digital metadata": "Metadatos digitales",
    "physical environmental clue": "Indicio ambiental fisico",
    "emotional evidence": "Prueba emocional",
    "social relationship web evidence": "Red de relaciones",
    "contradictory witness statements": "Declaraciones contradictorias",
    "object placement anomalies": "Anomalias de colocacion de objetos",
    "old case file inconsistency": "Inconsistencia del expediente antiguo",
    "incident summary": "Resumen del incidente",
    "responding officer": "Oficial responsable",
    "persons involved": "Personas involucradas",
    "scene observations": "Observaciones de la escena",
    "actions taken": "Acciones realizadas",
    summary: "Resumen",
    victim: "Victima",
    "lead officer": "Oficial principal",
    "badge #": "Placa #",
    "initial call time": "Hora de la llamada inicial",
    "initial assessment": "Evaluacion inicial",
    "primary scene": "Escena principal",
    "access notes": "Notas de acceso",
    "primary suspects": "Sospechosos principales",
    "autopsy details": "Detalles de la autopsia",
    examiner: "Medico forense",
    "estimated time of death": "Hora estimada de la muerte",
    "cause of death": "Causa de la muerte",
    "manner of death": "Modo de muerte",
    height: "Altura",
    weight: "Peso",
    "identifying marks": "Senales distintivas",
    "external findings": "Hallazgos externos",
    "internal findings": "Hallazgos internos",
    toxicology: "Toxicologia",
    "evidence recovered": "Evidencia recuperada",
    "examiner opinion": "Conclusion del forense",
  },
  de: {
    "case overview": "Fallubersicht",
    "victim context": "Hintergrund des Opfers",
    "key relationships": "Wichtige Beziehungen",
    "known tensions": "Bekannte Spannungen",
    "timeline pressure": "Zeitlicher Druck",
    "why the case matters now": "Warum der Fall jetzt wichtig ist",
    "time stamped surveillance gap": "Zeitversetzte Uberwachungslucke",
    "personal habit evidence": "Gewohnheitsbezogener Beleg",
    "misleading financial trail": "Irrefuhrende Finanzspur",
    "digital metadata": "Digitale Metadaten",
    "physical environmental clue": "Physischer Umweltbefund",
    "emotional evidence": "Emotionaler Beleg",
    "social relationship web evidence": "Beziehungsgeflecht",
    "contradictory witness statements": "Widerspruchliche Zeugenaussagen",
    "object placement anomalies": "Auffallige Objektplatzierung",
    "old case file inconsistency": "Unstimmigkeit in der Altakte",
    "incident summary": "Vorfallszusammenfassung",
    "responding officer": "Ermittelnder Beamter",
    "persons involved": "Beteiligte Personen",
    "scene observations": "Beobachtungen am Tatort",
    "actions taken": "Eingeleitete Massnahmen",
    summary: "Zusammenfassung",
    victim: "Opfer",
    "lead officer": "Leitender Ermittler",
    "badge #": "Dienstnummer",
    "initial call time": "Zeit des Notrufs",
    "initial assessment": "Ersteinschatzung",
    "primary scene": "Primarer Tatort",
    "access notes": "Zugangshinweise",
    "primary suspects": "Hauptverdachtige",
    "autopsy details": "Autopsiedetails",
    examiner: "Gerichtsmediziner",
    "estimated time of death": "Geschatzte Todeszeit",
    "cause of death": "Todesursache",
    "manner of death": "Todesart",
    height: "Grosse",
    weight: "Gewicht",
    "identifying marks": "Auffallige Merkmale",
    "external findings": "Aussere Befunde",
    "internal findings": "Innere Befunde",
    toxicology: "Toxikologie",
    "evidence recovered": "Gesicherte Spuren",
    "examiner opinion": "Gutachterliche Einschatzung",
  },
  it: {
    "case overview": "Panoramica del caso",
    "victim context": "Contesto della vittima",
    "key relationships": "Relazioni principali",
    "known tensions": "Tensioni note",
    "timeline pressure": "Pressione temporale",
    "why the case matters now": "Perche il caso conta ora",
    "time stamped surveillance gap": "Vuoto di sorveglianza con orario",
    "personal habit evidence": "Indizio di abitudine personale",
    "misleading financial trail": "Falsa pista finanziaria",
    "digital metadata": "Metadati digitali",
    "physical environmental clue": "Indizio ambientale fisico",
    "emotional evidence": "Reperto emotivo",
    "social relationship web evidence": "Rete di relazioni",
    "contradictory witness statements": "Dichiarazioni contraddittorie",
    "object placement anomalies": "Anomalie di collocazione degli oggetti",
    "old case file inconsistency": "Incoerenza del vecchio fascicolo",
    "incident summary": "Sintesi dell'incidente",
    "responding officer": "Agente responsabile",
    "persons involved": "Persone coinvolte",
    "scene observations": "Osservazioni sulla scena",
    "actions taken": "Azioni intraprese",
    summary: "Sintesi",
    victim: "Vittima",
    "lead officer": "Responsabile principale",
    "badge #": "Numero di distintivo",
    "initial call time": "Ora della chiamata iniziale",
    "initial assessment": "Valutazione iniziale",
    "primary scene": "Scena principale",
    "access notes": "Note di accesso",
    "primary suspects": "Sospetti principali",
    "autopsy details": "Dettagli dell'autopsia",
    examiner: "Medico legale",
    "estimated time of death": "Ora stimata del decesso",
    "cause of death": "Causa del decesso",
    "manner of death": "Modalita del decesso",
    height: "Altezza",
    weight: "Peso",
    "identifying marks": "Segni distintivi",
    "external findings": "Rilievi esterni",
    "internal findings": "Rilievi interni",
    toxicology: "Tossicologia",
    "evidence recovered": "Elementi repertati",
    "examiner opinion": "Valutazione del medico legale",
  },
};

const CUSTOM_CASE_VIEWER_COPY: Record<CustomCaseViewerLang, CustomCaseViewerCopy> = {
  en: {
    pageTitleSuffix: "Custom Case Access",
    brandName: "Cold Case Game Kits",
    customCaseFile: "Custom Case File",
    defaultTitle: "Your Custom Cold Case",
    defaultSummary: "This private case link opens your completed custom cold case once delivery is ready.",
    loadingMessage: "Loading your custom case access...",
    accessProblem: "Access Problem",
    evidenceFileNotFound: "Evidence file not found.",
    buildStatus: "Build Status",
    refresh: "Refresh",
    privateCaseTitle: "Your Custom Case | Private Access",
    privateCaseDescription: "This owner link opens the private version of your custom case. We will send a one-time email code before opening it on this browser.",
    privateCaseBadge: "Private Case Link",
    statusSummary: {
      delivery_ready: "Your custom case is ready. This private link now opens the full case experience and keeps your packet access fresh.",
      delivered: "Your custom case is ready. This private link now opens the full case experience and keeps your packet access fresh.",
      review_required: "The case build is in final review. This page will unlock the full case experience as soon as release is approved.",
      generation_running: "The case is still being assembled. Keep this private link and check back shortly.",
      generation_queued: "The case is still being assembled. Keep this private link and check back shortly.",
      generation_failed: "The last build attempt failed before completion. Return to your intake link to start a fresh build.",
      default: "Your private case link is active. This page will update automatically as the build moves forward.",
    },
    createPdf: "Create PDF",
    openCasePacket: "Open Case Packet",
    copyPrivateLink: "Copy Private Link",
    linkCopied: "Link Copied",
    privateCustomDelivery: "Private Custom Delivery",
    sharedCustomDelivery: "Shared Custom Delivery",
    accessMode: "Access Mode",
    ownerLink: "Owner Link",
    guestLink: "Guest Link",
    privateCustomAccessChip: "Private Custom Access",
    guestShareAccessChip: "Guest Share Access",
    evidenceVolume: "Evidence Volume",
    sections: buildCountLabel("section", "sections"),
    evidenceRecords: buildCountLabel("evidence record", "evidence records"),
    imageAssets: buildCountLabel("image asset", "image assets"),
    caseState: "Case State",
    suspects: "Suspects",
    evidenceFiles: "Evidence Files",
    caseHub: "Case Hub",
    caseHubBody: "This private page stays stable even when the packet URL rotates. Reopen it any time from your email.",
    detectiveBriefing: "Detective Briefing",
    whatOpensHere: "What Opens Here",
    fullCaseExperience: "Full Case Experience",
    fullCaseExperienceBody: "When delivery is ready, this same private link opens the complete case experience instead of a summary hub.",
    privateLinkCard: "Private Link",
    privateLinkCardBody: "This page refreshes the signed packet access for you, so the email link stays useful after the PDF link expires.",
    customCaseEvidence: "Custom Case Evidence",
    backToCaseFile: "Back to Case File",
    openPdfPacket: "Open PDF Packet",
    metadata: {
      date: "Date",
      source: "Source",
      confidentiality: "Confidentiality",
      evidenceType: "Evidence Type",
      noDate: "No date",
      noSource: "No source",
      internal: "internal",
    },
    pager: {
      previousFile: "Previous File",
      nextFile: "Next File",
      previousEvidence: "Previous evidence",
      nextEvidence: "Next evidence",
    },
    warrant: {
      detectiveLabel: "Detective",
      detectiveRank: "Custom Case Detective",
      approved: "Warrant approved. Your suspect choice was correct and the full solution is now unlocked.",
      rejected: "That suspect is not correct. Review the evidence and file another warrant request.",
      reset: "Warrant state reset for local testing.",
    },
    share: {
      sectionEyebrow: "Guest Sharing",
      sectionTitle: "Create a separate guest link",
      sectionBody: "Share a guest link instead of your owner URL. Guest links expire after 24 hours, can be revoked, and do not expose your intake or owner access flow.",
      expiresIn24Hours: "Guest links expire in 24 hours.",
      createIdle: "Create Guest Link",
      createBusy: "Creating Guest Link...",
      newestLink: "Newest guest link",
      copyLink: "Copy Guest Link",
      copied: "Guest link copied.",
      created: "Guest link created. Share this link instead of your owner URL.",
      revoked: "Guest link revoked.",
      revoke: "Revoke Guest Link",
      noActiveLinks: "No active guest links yet.",
      guestLinkFallback: "Guest Share Link",
      expiresPrefix: "Expires",
      expiresSoon: "soon",
      accessCount: buildCountLabel("access", "accesses"),
    },
    errors: {
      loadCase: "Unable to load your custom case.",
      createGuestLink: "Unable to create guest link.",
      copyGuestLink: "Unable to copy guest link.",
      revokeGuestLink: "Unable to revoke guest link.",
      submitWarrant: "Failed to submit warrant.",
      downloadPdf: "Failed to download case PDF.",
      resetWarrant: "Failed to reset warrant state.",
    },
    video: {
      eyebrow: "Custom Cold Case",
      title: "Welcome Video",
      open: "Open Welcome Video",
      empty: "No briefing video is attached to this case yet.",
      iframeTitle: "Briefing video",
    },
    briefing: {
      eyebrow: "Assignment Notes",
      title: "Briefing Notes",
      buildStatusLink: "Build Status",
      defaultBody: "Review the briefing, inspect each file, and build your theory before naming the most likely suspect.",
      setting: "Setting",
      unknownPlace: "Unknown place",
      unknownTime: "Unknown time",
      delivery: "Delivery",
      deliveryValue: "CUSTOM CASE ACCESS",
      deliveryBody: "This private link does not require an Agency membership.",
    },
    archive: {
      eyebrow: "Field Archive",
      title: "Evidence Index",
      accessLabel: "Custom Access",
      openFile: "Open file",
      includedSuspectDossiers: "Included Suspect Dossiers",
    },
    suspectDossiers: {
      eyebrow: "Suspect Dossiers",
      title: "Everyone In The File",
      count: buildCountLabel("suspect", "suspects"),
      fallbackName: (index) => `Suspect ${index + 1}`,
    },
    ownerGate: {
      title: "Verify Your Private Case Link",
      description: "This owner link opens the private version of your custom case. We will send a one-time email code before opening it on this browser.",
      copy: {
        loading: "Checking private access...",
        badge: "Private Owner Access",
        sendCodeToOrderEmail: "Send the code to the order email.",
        rememberedNotice: "We only ask for this on a new browser or device. After verification, this browser stays remembered for future visits.",
        requestIdle: "Email Access Code",
        requestBusy: "Sending Code...",
        codeLabel: "6-digit code",
        codePlaceholder: "123456",
        verifyIdle: "Verify And Continue",
        verifyBusy: "Verifying...",
        clearRememberedAccess: "Clear Remembered Access",
        devCodePreview: "Dev code preview",
        invalidCode: "Enter the 6-digit code from your email.",
        statusCodeSent: "Access code sent. Enter it below to continue.",
        statusVerified: "Access confirmed for this browser.",
        statusCleared: "Remembered access cleared for this browser.",
        loadAccessStateError: "Unable to load access state.",
        requestCodeError: "Unable to send access code.",
        verifyCodeError: "Unable to verify access code.",
        clearAccessError: "Unable to clear remembered access.",
      },
    },
    printModal: {
      eyebrow: "Download PDF",
      title: "Prepare the case file",
      description: "Choose the paper theme and evidence set, then download the server-rendered PDF.",
      close: "Close",
      theme: "Theme",
      themeLight: "light",
      themeLightDescription: "Paper-first output with brighter cards and lower ink density.",
      themeDark: "dark",
      themeDarkDescription: "Dramatic dark packet for screen-first review and premium stock printing.",
      evidenceSelection: "Evidence Selection",
      allEvidenceIncluded: "All evidence files will be included.",
      selectedEvidence: (count) => `${count} evidence file(s) selected.`,
      deselectAll: "Deselect all",
      selectAll: "Select all",
      fileLabel: (index) => `File ${String(index + 1).padStart(2, "0")}`,
      footer: "PDF downloads use the dedicated print document renderer rather than the interactive case layout.",
      downloadIdle: "Download PDF",
      downloadBusy: "Preparing PDF...",
    },
    media: {
      close: "Close",
      imageAlt: (index) => `Evidence image ${index + 1}`,
      openLarge: "Open Large",
      signedPreviewUnavailable: "Signed preview unavailable",
    },
    panels: {
      localizedEvidenceLabels: CUSTOM_CASE_EVIDENCE_LABELS.en,
      photoPacket: "Photo Packet",
      crimeScenePhotos: "Crime Scene Photos",
      imageCount: buildCountLabel("image", "images"),
      noCrimeScenePhotos: "No crime scene photos linked yet.",
      witnessStatements: "Witness Statements",
      associatedWitness: "Associated Witness",
      personOfInterest: "Person of interest",
      statementCount: buildCountLabel("statement", "statements"),
      noAssociatedPortrait: "No associated portrait",
      additionalStatements: "Additional Statements",
      victimProfile: "Victim Profile",
      identitySummary: "Identity Summary",
      dossier: "dossier",
      victimPortrait: "Victim portrait",
      portraitPendingVictim: "Portrait pending. Upload a victim image to complete this dossier.",
      noStructuredProfileFields: "No structured profile fields generated yet.",
      noVictimNarrative:
        "No victim narrative text was generated yet. The profile currently contains structured identity fields only.",
      victimProfiles: "Victim Profiles",
      suspectDossiers: "Suspect Dossiers",
      suspectCount: buildCountLabel("suspect", "suspects"),
      openDossier: "Open Dossier",
      closeDossier: "Close Dossier",
      portraitPendingSuspect: "Portrait pending. Upload a suspect image to complete this dossier.",
      suspectEvidence: "Suspect Evidence",
      noSuspectEvidence:
        "No suspect evidence has been authored yet. Add alibi, evidence pressure, and opportunity details in the suspect editor.",
      witnessMentions: "Witness Mentions",
      witnessMentionCount: buildCountLabel("witness mention", "witness mentions"),
      noSuspects: "No suspects attached to this draft yet.",
      noRoleProvided: "No role provided",
      motive: "Motive",
      age: "Age",
      sex: "Sex",
      hair: "Hair",
      eyes: "Eyes",
      height: "Height",
      build: "Build",
      maritalStatus: "Marital Status",
      occupation: "Occupation",
      employer: "Employer",
      alibi: "Alibi",
      evidenceAgainst: "Evidence Against",
      evidenceInFavor: "Evidence In Favor",
      opportunity: "Opportunity",
      behaviorNotes: "Behavior Notes",
      noTextChainsAvailable: "No text chains available.",
      recoveredTextChain: (index) => `Recovered Text Chain ${index + 1}`,
      messageChains: "Message Chains",
      messageCount: (count) => `${count} msgs`,
      chainLabel: (index) => `Chain ${index + 1}`,
      recoveredEmail: (index) => `Recovered Email ${index + 1}`,
      subjectLine: "Subject Line",
      universityMailArchive: "University Mail Archive",
      emailThread: "Email Thread",
      from: "From",
      to: "To",
      cc: "CC",
      date: "Date",
      recoveredFromArchiveExport: "Recovered from archive export",
      archivedPress: "Archived Press",
      newspaperCoverage: "Newspaper Coverage",
      pressClip: "Press Clip",
      byline: "Byline",
      staffReporter: "Staff Reporter",
      undatedClipping: "Undated clipping",
      archiveReproduction: "Archive reproduction",
      ottawaPoliceService: "Ottawa Police Service",
      majorCrimesDivision: "Major Crimes Division",
      incidentReport: "Incident Report",
      filedDocumentSummary: "Filed document summary",
      internalUse: "Internal Use",
      fileLabel: (caseNumber) => `File ${caseNumber}`,
      caseNumber: "Case Number",
      pendingAssignment: "Pending assignment",
      incidentWindow: "Incident Window",
      seeReportBody: "See report body",
      bodyDiscovery: "Body Discovery",
      officeChiefForensicPathologist: "Office of the Chief Forensic Pathologist",
      forensicPathologyUnit: "Forensic Pathology Unit",
      postMortemExaminationReport: "Post-Mortem Examination Report",
      autopsySummary:
        "Forensic summary of injuries, physiological findings, and examiner conclusions recorded at autopsy.",
      medicalExaminerCopy: "Medical Examiner Copy",
      caseLabel: (caseNumber) => `Case ${caseNumber}`,
      decedent: "Decedent",
      notListed: "Not listed",
      caseFile: "Case File",
      pending: "Pending",
      sectionLabel: (index) => `Section ${String(index + 1).padStart(2, "0")}`,
      imagePrompt: "Image Prompt",
      portraitPrompt: "Portrait Prompt",
    },
    solution: {
      finalRevealEyebrow: "Final Reveal",
      readyTitle: "Read This Only When You're Ready To Solve The Case",
      readyDescription: "This file contains the full answer, the culprit, and the reasoning chain. Open it only after you have made your final accusation.",
      warningEyebrow: "Warning",
      warningDescription: "Once you open the solution, the mystery is gone. Pause here if your group still wants to debate suspects, motive, means, and opportunity.",
      arrestWarrant: "Arrest Warrant",
      greetingCustom: (actorLabel) => `Congratulations on reaching the end of the case, ${actorLabel}.`,
      greetingMember: (rankLabel, actorLabel) => `Congratulations on reaching the end of the case, ${rankLabel} ${actorLabel}.`,
      arrestPrompt: "Now is the time to request an arrest warrant. Select the suspect you believe masterminded this crime.",
      suspectSelection: "Suspect Selection",
      selectedSuspect: "Selected suspect",
      noPhoto: "No Photo",
      suspectDossier: "Suspect dossier",
      submitting: "Submitting...",
      warrantApproved: "Warrant Approved",
      warrantRequest: "Submit Warrant Request",
      warrantFiled: "Warrant Filed",
      previewOnly: "Preview Only",
      previewOnlyDescription: "Preview mode only. Suspect selection is shown here for content verification.",
      attempts: "Attempts",
      filedSuspect: "Filed suspect",
      unknownSuspect: "Unknown suspect",
      result: "Result",
      correctWarrantApproved: "Correct warrant approved",
      incorrectSuspectTryAgain: "Incorrect suspect. Try again",
      correctFirstWarrant: "Correct first warrant",
      solutionUnlockedWithoutSolveCredit: "Solution unlocked without solve credit",
      recordSummary: (count, nextRankLabel, solvesToNextRank) =>
        `Record: ${count} successful solve${count === 1 ? "" : "s"}${
          nextRankLabel ? `, ${Number(solvesToNextRank || 0)} until ${nextRankLabel}.` : "."
        }`,
      customSolutionUnlocked: "The official solution is now unlocked for this custom case.",
      customRetryPrompt: "That warrant was not correct. Review the evidence and file another warrant request.",
      guestSessionUnlocked: "This guest-session result unlocks the solution for this session only.",
      solutionAccess: "Solution Access",
      openOfficialSolution: "Open The Official Case Solution",
      reveal: "Reveal",
      hide: "Hide",
      submitCorrectPrompt: "Submit the correct warrant above to unlock the official solution.",
      submitWarrantPrompt: "Submit a warrant request above to unlock the official solution.",
      shareTheWin: "Share The Win",
      shareDescription: "Post your result with one tap or copy the message below for social media.",
      shareNow: "Share Now",
      copied: "Copied",
      copyFailed: "Copy Failed",
      copyShareText: "Copy Share Text",
      shareTextCustom: (caseTitle) => `ColdCaseGameKits.com - Just solved our custom cold case "${caseTitle}"`,
      shareTextAgency: (caseTitle) => `We solved "${caseTitle}" at the Cold Case Agency. ${COLD_CASE_WEBSITE_URL}`,
      nativeShareTitle: (caseTitle) => `We solved "${caseTitle}"`,
    },
  },
  fr: {} as CustomCaseViewerCopy,
  es: {} as CustomCaseViewerCopy,
  de: {} as CustomCaseViewerCopy,
  it: {} as CustomCaseViewerCopy,
};

CUSTOM_CASE_VIEWER_COPY.fr = {
  ...CUSTOM_CASE_VIEWER_COPY.en,
  pageTitleSuffix: "Acces affaire personnalisee",
  customCaseFile: "Dossier personnalise",
  defaultTitle: "Votre affaire personnalisee",
  defaultSummary: "Ce lien prive ouvre votre affaire personnalisee des qu'elle est prete.",
  loadingMessage: "Chargement de votre acces prive...",
  accessProblem: "Probleme d'acces",
  evidenceFileNotFound: "Fichier de preuve introuvable.",
  buildStatus: "Statut du build",
  refresh: "Actualiser",
  privateCaseBadge: "Lien prive",
  privateCustomDelivery: "Livraison privee personnalisee",
  sharedCustomDelivery: "Livraison partagee personnalisee",
  accessMode: "Mode d'acces",
  ownerLink: "Lien proprietaire",
  guestLink: "Lien invite",
  privateCustomAccessChip: "Acces prive personnalise",
  guestShareAccessChip: "Acces partage invite",
  evidenceVolume: "Volume de preuves",
  sections: (count) => `${count} section${count === 1 ? "" : "s"}`,
  evidenceRecords: (count) => `${count} preuve${count === 1 ? "" : "s"}`,
  imageAssets: (count) => `${count} ressource${count === 1 ? " image" : "s image"}`,
  detectiveBriefing: "Briefing detective",
  whatOpensHere: "Ce qui s'ouvre ici",
  fullCaseExperience: "Experience complete",
  privateLinkCard: "Lien prive",
  customCaseEvidence: "Preuve du dossier personnalise",
  backToCaseFile: "Retour au dossier",
  openPdfPacket: "Ouvrir le PDF",
  createPdf: "Creer le PDF",
  metadata: {
    date: "Date",
    source: "Source",
    confidentiality: "Confidentialite",
    evidenceType: "Type de preuve",
    noDate: "Sans date",
    noSource: "Sans source",
    internal: "interne",
  },
  pager: {
    previousFile: "Fichier precedent",
    nextFile: "Fichier suivant",
    previousEvidence: "Preuve precedente",
    nextEvidence: "Preuve suivante",
  },
  warrant: {
    detectiveLabel: "Detective",
    detectiveRank: "Detective du dossier personnalise",
    approved: "Mandat approuve. Votre choix etait correct et la solution complete est maintenant ouverte.",
    rejected: "Ce suspect n'est pas correct. Relisez les preuves et deposez un nouveau mandat.",
    reset: "Etat du mandat reinitialise pour les tests locaux.",
  },
  share: {
    ...CUSTOM_CASE_VIEWER_COPY.en.share,
    sectionEyebrow: "Partage invite",
    sectionTitle: "Creer un lien invite separe",
    sectionBody: "Partagez un lien invite au lieu de votre URL proprietaire. Les liens invites expirent apres 24 heures, peuvent etre revoques et n'exposent pas votre intake.",
    expiresIn24Hours: "Les liens invites expirent apres 24 heures.",
    createIdle: "Creer un lien invite",
    createBusy: "Creation du lien invite...",
    newestLink: "Dernier lien invite",
    copyLink: "Copier le lien invite",
    copied: "Lien invite copie.",
    created: "Lien invite cree. Partagez ce lien au lieu de votre URL proprietaire.",
    revoked: "Lien invite revoque.",
    revoke: "Revoquer le lien invite",
    noActiveLinks: "Aucun lien invite actif pour le moment.",
    guestLinkFallback: "Lien invite",
    expiresPrefix: "Expire",
    expiresSoon: "bientot",
    accessCount: (count) => `${count} acces`,
  },
  errors: {
    loadCase: "Impossible de charger votre affaire personnalisee.",
    createGuestLink: "Impossible de creer un lien invite.",
    copyGuestLink: "Impossible de copier le lien invite.",
    revokeGuestLink: "Impossible de revoquer le lien invite.",
    submitWarrant: "Impossible d'envoyer le mandat.",
    downloadPdf: "Impossible de telecharger le PDF du dossier.",
    resetWarrant: "Impossible de reinitialiser l'etat du mandat.",
  },
  video: {
    eyebrow: "Affaire personnalisee",
    title: "Video d'accueil",
    open: "Ouvrir la video d'accueil",
    empty: "Aucune video de briefing n'est encore attachee a cette affaire.",
    iframeTitle: "Video de briefing",
  },
  briefing: {
    eyebrow: "Notes de mission",
    title: "Notes de briefing",
    buildStatusLink: "Statut du build",
    defaultBody: "Lisez le briefing, examinez chaque fichier et construisez votre theorie avant de nommer le suspect le plus probable.",
    setting: "Cadre",
    unknownPlace: "Lieu inconnu",
    unknownTime: "Heure inconnue",
    delivery: "Livraison",
    deliveryValue: "ACCES AFFAIRE PERSONNALISEE",
    deliveryBody: "Ce lien prive ne demande pas d'abonnement Agency.",
  },
  archive: {
    eyebrow: "Archives de terrain",
    title: "Index des preuves",
    accessLabel: "Acces personnalise",
    openFile: "Ouvrir le fichier",
    includedSuspectDossiers: "Dossiers suspects inclus",
  },
  suspectDossiers: {
    eyebrow: "Dossiers suspects",
    title: "Tout le monde dans le dossier",
    count: (count) => `${count} suspect${count === 1 ? "" : "s"}`,
    fallbackName: (index) => `Suspect ${index + 1}`,
  },
  ownerGate: {
    title: "Verifiez votre lien prive",
    description: "Ce lien proprietaire ouvre la version privee de votre affaire personnalisee. Nous vous enverrons un code unique par email avant de l'ouvrir sur ce navigateur.",
    copy: {
      loading: "Verification de l'acces prive...",
      badge: "Acces proprietaire prive",
      sendCodeToOrderEmail: "Envoyer le code a l'email de commande.",
      rememberedNotice: "Nous le demandons seulement sur un nouveau navigateur ou appareil. Apres verification, ce navigateur reste memorise.",
      requestIdle: "Envoyer le code d'acces",
      requestBusy: "Envoi du code...",
      codeLabel: "Code a 6 chiffres",
      codePlaceholder: "123456",
      verifyIdle: "Verifier et continuer",
      verifyBusy: "Verification...",
      clearRememberedAccess: "Effacer l'acces memorise",
      devCodePreview: "Code dev",
      invalidCode: "Entrez le code a 6 chiffres recu par email.",
      statusCodeSent: "Code envoye. Entrez-le ci-dessous pour continuer.",
      statusVerified: "Acces confirme pour ce navigateur.",
      statusCleared: "Acces memorise efface pour ce navigateur.",
      loadAccessStateError: "Impossible de charger l'etat d'acces.",
      requestCodeError: "Impossible d'envoyer le code d'acces.",
      verifyCodeError: "Impossible de verifier le code d'acces.",
      clearAccessError: "Impossible d'effacer l'acces memorise.",
    },
  },
  printModal: {
    ...CUSTOM_CASE_VIEWER_COPY.en.printModal,
    eyebrow: "Telecharger le PDF",
    title: "Preparer le dossier",
    description: "Choisissez le theme papier et les preuves, puis telechargez le PDF rendu par le serveur.",
    close: "Fermer",
    theme: "Theme",
    themeLightDescription: "Sortie orientee papier avec des cartes plus claires et moins d'encre.",
    themeDarkDescription: "Packet sombre pour revision a l'ecran et impression premium.",
    evidenceSelection: "Selection des preuves",
    allEvidenceIncluded: "Tous les fichiers de preuve seront inclus.",
    selectedEvidence: (count) => `${count} fichier(s) de preuve selectionne(s).`,
    deselectAll: "Tout deselectionner",
    selectAll: "Tout selectionner",
    footer: "Le telechargement PDF utilise le rendu dedie plutot que la mise en page interactive.",
    downloadIdle: "Telecharger le PDF",
    downloadBusy: "Preparation du PDF...",
  },
    media: {
      close: "Fermer",
      imageAlt: (index) => `Image de preuve ${index + 1}`,
      openLarge: "Ouvrir en grand",
      signedPreviewUnavailable: "Apercu signe indisponible",
    },
    panels: {
      localizedEvidenceLabels: CUSTOM_CASE_EVIDENCE_LABELS.fr,
      photoPacket: "Paquet photo",
      crimeScenePhotos: "Photos de la scene",
      imageCount: buildCountLabel("image", "images"),
      noCrimeScenePhotos: "Aucune photo de scene n'est encore liee.",
      witnessStatements: "Declarations des temoins",
      associatedWitness: "Temoin associe",
      personOfInterest: "Personne d'interet",
      statementCount: buildCountLabel("declaration", "declarations"),
      noAssociatedPortrait: "Aucun portrait associe",
      additionalStatements: "Declarations supplementaires",
      victimProfile: "Profil de la victime",
      identitySummary: "Resume d'identite",
      dossier: "dossier",
      victimPortrait: "Portrait de la victime",
      portraitPendingVictim: "Portrait en attente. Ajoutez une image de victime pour completer ce dossier.",
      noStructuredProfileFields: "Aucun champ de profil structure n'a encore ete genere.",
      noVictimNarrative:
        "Aucun recit de victime n'a encore ete genere. Le profil contient pour l'instant seulement des champs d'identite structures.",
      victimProfiles: "Profils de la victime",
      suspectDossiers: "Dossiers des suspects",
      suspectCount: buildCountLabel("suspect", "suspects"),
      openDossier: "Ouvrir le dossier",
      closeDossier: "Fermer le dossier",
      portraitPendingSuspect: "Portrait en attente. Ajoutez une image de suspect pour completer ce dossier.",
      suspectEvidence: "Preuves contre le suspect",
      noSuspectEvidence:
        "Aucune preuve contre le suspect n'a encore ete redigee. Ajoutez l'alibi, la pression des preuves et les details d'opportunite dans l'editeur du suspect.",
      witnessMentions: "Mentions de temoins",
      witnessMentionCount: buildCountLabel("mention de temoin", "mentions de temoins"),
      noSuspects: "Aucun suspect n'est encore attache a cette ebauche.",
      noRoleProvided: "Aucun role fourni",
      motive: "Mobile",
      age: "Age",
      sex: "Sexe",
      hair: "Cheveux",
      eyes: "Yeux",
      height: "Taille",
      build: "Carrure",
      maritalStatus: "Situation matrimoniale",
      occupation: "Profession",
      employer: "Employeur",
      alibi: "Alibi",
      evidenceAgainst: "Preuves a charge",
      evidenceInFavor: "Preuves a decharge",
      opportunity: "Occasion",
      behaviorNotes: "Notes de comportement",
      noTextChainsAvailable: "Aucune chaine de messages disponible.",
      recoveredTextChain: (index) => `Chaine de messages recuperee ${index + 1}`,
      messageChains: "Chaines de messages",
      messageCount: (count) => `${count} msgs`,
      chainLabel: (index) => `Chaine ${index + 1}`,
      recoveredEmail: (index) => `Email recupere ${index + 1}`,
      subjectLine: "Objet",
      universityMailArchive: "Archives du courrier universitaire",
      emailThread: "Fil d'email",
      from: "De",
      to: "A",
      cc: "CC",
      date: "Date",
      recoveredFromArchiveExport: "Recupere depuis l'export des archives",
      archivedPress: "Presse archivee",
      newspaperCoverage: "Couverture de presse",
      pressClip: "Coupure de presse",
      byline: "Signature",
      staffReporter: "Redaction",
      undatedClipping: "Coupure non datee",
      archiveReproduction: "Reproduction d'archive",
      ottawaPoliceService: "Service de police d'Ottawa",
      majorCrimesDivision: "Division des crimes majeurs",
      incidentReport: "Rapport d'incident",
      filedDocumentSummary: "Resume du document depose",
      internalUse: "Usage interne",
      fileLabel: (caseNumber) => `Dossier ${caseNumber}`,
      caseNumber: "Numero de dossier",
      pendingAssignment: "Attribution en attente",
      incidentWindow: "Fenetre de l'incident",
      seeReportBody: "Voir le corps du rapport",
      bodyDiscovery: "Decouverte du corps",
      officeChiefForensicPathologist: "Bureau du pathologiste en chef",
      forensicPathologyUnit: "Unite de pathologie medico-legale",
      postMortemExaminationReport: "Rapport d'examen post-mortem",
      autopsySummary:
        "Resume medico-legal des blessures, constatations physiologiques et conclusions de l'examinateur relevees a l'autopsie.",
      medicalExaminerCopy: "Copie du medecin legiste",
      caseLabel: (caseNumber) => `Affaire ${caseNumber}`,
      decedent: "Defunt",
      notListed: "Non indique",
      caseFile: "Dossier",
      pending: "En attente",
      sectionLabel: (index) => `Section ${String(index + 1).padStart(2, "0")}`,
      imagePrompt: "Prompt d'image",
      portraitPrompt: "Prompt de portrait",
    },
    solution: {
    ...CUSTOM_CASE_VIEWER_COPY.en.solution,
    finalRevealEyebrow: "Revelation finale",
    readyTitle: "Lisez ceci seulement quand vous etes pret a resoudre l'affaire",
    readyDescription: "Ce fichier contient la reponse complete, le coupable et la logique de l'affaire. Ouvrez-le seulement apres votre accusation finale.",
    warningEyebrow: "Avertissement",
    warningDescription: "Une fois la solution ouverte, le mystere disparait. Faites une pause si votre groupe veut encore debattre des suspects, du mobile, des moyens et de l'occasion.",
    arrestWarrant: "Mandat d'arrestation",
    greetingCustom: (actorLabel) => `Felicitations d'etre arrive a la fin de l'affaire, ${actorLabel}.`,
    greetingMember: (rankLabel, actorLabel) => `Felicitations d'etre arrive a la fin de l'affaire, ${rankLabel} ${actorLabel}.`,
    arrestPrompt: "C'est le moment de demander un mandat. Choisissez le suspect que vous croyez responsable de ce crime.",
    suspectSelection: "Selection du suspect",
    selectedSuspect: "Suspect selectionne",
    noPhoto: "Sans photo",
    suspectDossier: "Dossier suspect",
    submitting: "Envoi...",
    warrantApproved: "Mandat approuve",
    warrantRequest: "Soumettre la demande de mandat",
    warrantFiled: "Mandat depose",
    previewOnly: "Apercu seulement",
    previewOnlyDescription: "Mode apercu seulement. La selection du suspect est affichee ici pour verifier le contenu.",
    attempts: "Tentatives",
    filedSuspect: "Suspect depose",
    unknownSuspect: "Suspect inconnu",
    result: "Resultat",
    correctWarrantApproved: "Mandat correct approuve",
    incorrectSuspectTryAgain: "Suspect incorrect. Reessayez.",
    correctFirstWarrant: "Premier mandat correct",
    solutionUnlockedWithoutSolveCredit: "Solution ouverte sans credit de resolution",
    recordSummary: (count, nextRankLabel, solvesToNextRank) =>
      `Dossier: ${count} resolution${count === 1 ? "" : "s"} reussie${count === 1 ? "" : "s"}${
        nextRankLabel ? `, ${Number(solvesToNextRank || 0)} avant ${nextRankLabel}.` : "."
      }`,
    customSolutionUnlocked: "La solution officielle est maintenant ouverte pour cette affaire personnalisee.",
    customRetryPrompt: "Ce mandat n'etait pas correct. Revoyez les preuves et deposez une autre demande.",
    guestSessionUnlocked: "Ce resultat invite ouvre la solution seulement pour cette session.",
    solutionAccess: "Acces a la solution",
    openOfficialSolution: "Ouvrir la solution officielle",
    reveal: "Reveler",
    hide: "Masquer",
    submitCorrectPrompt: "Soumettez le bon mandat ci-dessus pour ouvrir la solution officielle.",
    submitWarrantPrompt: "Soumettez une demande de mandat ci-dessus pour ouvrir la solution officielle.",
    shareTheWin: "Partager la victoire",
    shareDescription: "Publiez votre resultat en un geste ou copiez le message ci-dessous pour les reseaux sociaux.",
    shareNow: "Partager",
    copied: "Copie",
    copyFailed: "Echec de copie",
    copyShareText: "Copier le texte",
    shareTextCustom: (caseTitle) => `ColdCaseGameKits.com - Nous avons resolu notre affaire personnalisee "${caseTitle}"`,
    shareTextAgency: (caseTitle) => `Nous avons resolu "${caseTitle}" a la Cold Case Agency. ${COLD_CASE_WEBSITE_URL}`,
    nativeShareTitle: (caseTitle) => `Nous avons resolu "${caseTitle}"`,
  },
};

CUSTOM_CASE_VIEWER_COPY.es = {
  ...CUSTOM_CASE_VIEWER_COPY.en,
  pageTitleSuffix: "Acceso al caso personalizado",
  customCaseFile: "Expediente personalizado",
  defaultTitle: "Tu caso personalizado",
  defaultSummary: "Este enlace privado abre tu caso personalizado cuando este listo.",
  loadingMessage: "Cargando tu acceso al caso...",
  accessProblem: "Problema de acceso",
  evidenceFileNotFound: "No se encontro el archivo de prueba.",
  buildStatus: "Estado del build",
  refresh: "Actualizar",
  privateCaseBadge: "Enlace privado",
  privateCustomDelivery: "Entrega privada personalizada",
  sharedCustomDelivery: "Entrega compartida personalizada",
  accessMode: "Modo de acceso",
  ownerLink: "Enlace del propietario",
  guestLink: "Enlace de invitado",
  privateCustomAccessChip: "Acceso privado personalizado",
  guestShareAccessChip: "Acceso compartido de invitado",
  evidenceVolume: "Volumen de pruebas",
  sections: (count) => `${count} seccion${count === 1 ? "" : "es"}`,
  evidenceRecords: (count) => `${count} registro${count === 1 ? " de prueba" : "s de prueba"}`,
  imageAssets: (count) => `${count} recurso${count === 1 ? " de imagen" : "s de imagen"}`,
  detectiveBriefing: "Briefing del detective",
  whatOpensHere: "Que se abre aqui",
  fullCaseExperience: "Experiencia completa",
  privateLinkCard: "Enlace privado",
  customCaseEvidence: "Prueba del caso personalizado",
  backToCaseFile: "Volver al expediente",
  openPdfPacket: "Abrir PDF",
  createPdf: "Crear PDF",
  metadata: {
    date: "Fecha",
    source: "Fuente",
    confidentiality: "Confidencialidad",
    evidenceType: "Tipo de prueba",
    noDate: "Sin fecha",
    noSource: "Sin fuente",
    internal: "interno",
  },
  pager: {
    previousFile: "Archivo anterior",
    nextFile: "Archivo siguiente",
    previousEvidence: "Prueba anterior",
    nextEvidence: "Prueba siguiente",
  },
  warrant: {
    detectiveLabel: "Detective",
    detectiveRank: "Detective del caso personalizado",
    approved: "Orden aprobada. Tu eleccion fue correcta y la solucion completa ya esta abierta.",
    rejected: "Ese sospechoso no es correcto. Revisa las pruebas y presenta otra orden.",
    reset: "Estado de la orden reiniciado para pruebas locales.",
  },
  share: {
    ...CUSTOM_CASE_VIEWER_COPY.en.share,
    sectionEyebrow: "Compartir con invitados",
    sectionTitle: "Crear un enlace de invitado aparte",
    sectionBody: "Comparte un enlace de invitado en lugar de tu URL de propietario. Los enlaces expiran en 24 horas, pueden revocarse y no exponen tu formulario.",
    expiresIn24Hours: "Los enlaces de invitado caducan en 24 horas.",
    createIdle: "Crear enlace de invitado",
    createBusy: "Creando enlace de invitado...",
    newestLink: "Enlace de invitado mas reciente",
    copyLink: "Copiar enlace de invitado",
    copied: "Enlace de invitado copiado.",
    created: "Enlace de invitado creado. Comparte este enlace en lugar de tu URL de propietario.",
    revoked: "Enlace de invitado revocado.",
    revoke: "Revocar enlace de invitado",
    noActiveLinks: "Todavia no hay enlaces de invitado activos.",
    guestLinkFallback: "Enlace de invitado",
    expiresPrefix: "Caduca",
    expiresSoon: "pronto",
    accessCount: (count) => `${count} acceso${count === 1 ? "" : "s"}`,
  },
  errors: {
    loadCase: "No se pudo cargar tu caso personalizado.",
    createGuestLink: "No se pudo crear el enlace de invitado.",
    copyGuestLink: "No se pudo copiar el enlace de invitado.",
    revokeGuestLink: "No se pudo revocar el enlace de invitado.",
    submitWarrant: "No se pudo enviar la orden.",
    downloadPdf: "No se pudo descargar el PDF del caso.",
    resetWarrant: "No se pudo reiniciar el estado de la orden.",
  },
  video: {
    eyebrow: "Caso personalizado",
    title: "Video de bienvenida",
    open: "Abrir video de bienvenida",
    empty: "Todavia no hay un video de briefing adjunto a este caso.",
    iframeTitle: "Video de briefing",
  },
  briefing: {
    eyebrow: "Notas de asignacion",
    title: "Notas del briefing",
    buildStatusLink: "Estado del build",
    defaultBody: "Revisa el briefing, inspecciona cada archivo y construye tu teoria antes de nombrar al sospechoso mas probable.",
    setting: "Escenario",
    unknownPlace: "Lugar desconocido",
    unknownTime: "Hora desconocida",
    delivery: "Entrega",
    deliveryValue: "ACCESO A CASO PERSONALIZADO",
    deliveryBody: "Este enlace privado no requiere una membresia Agency.",
  },
  archive: {
    eyebrow: "Archivo de campo",
    title: "Indice de pruebas",
    accessLabel: "Acceso personalizado",
    openFile: "Abrir archivo",
    includedSuspectDossiers: "Dossieres de sospechosos incluidos",
  },
  suspectDossiers: {
    eyebrow: "Dossieres de sospechosos",
    title: "Todos en el expediente",
    count: (count) => `${count} sospechoso${count === 1 ? "" : "s"}`,
    fallbackName: (index) => `Sospechoso ${index + 1}`,
  },
  ownerGate: {
    title: "Verifica tu enlace privado",
    description: "Este enlace de propietario abre la version privada de tu caso personalizado. Te enviaremos un codigo unico por email antes de abrirlo en este navegador.",
    copy: {
      loading: "Comprobando el acceso privado...",
      badge: "Acceso privado del propietario",
      sendCodeToOrderEmail: "Enviar el codigo al email del pedido.",
      rememberedNotice: "Solo lo pedimos en un navegador o dispositivo nuevo. Despues de verificar, este navegador quedara recordado.",
      requestIdle: "Enviar codigo de acceso",
      requestBusy: "Enviando codigo...",
      codeLabel: "Codigo de 6 digitos",
      codePlaceholder: "123456",
      verifyIdle: "Verificar y continuar",
      verifyBusy: "Verificando...",
      clearRememberedAccess: "Borrar acceso recordado",
      devCodePreview: "Codigo dev",
      invalidCode: "Introduce el codigo de 6 digitos que recibiste por email.",
      statusCodeSent: "Codigo enviado. Introducelo abajo para continuar.",
      statusVerified: "Acceso confirmado para este navegador.",
      statusCleared: "Acceso recordado borrado para este navegador.",
      loadAccessStateError: "No se pudo cargar el estado de acceso.",
      requestCodeError: "No se pudo enviar el codigo de acceso.",
      verifyCodeError: "No se pudo verificar el codigo de acceso.",
      clearAccessError: "No se pudo borrar el acceso recordado.",
    },
  },
  printModal: {
    ...CUSTOM_CASE_VIEWER_COPY.en.printModal,
    eyebrow: "Descargar PDF",
    title: "Preparar el expediente",
    description: "Elige el tema en papel y las pruebas, luego descarga el PDF generado por el servidor.",
    close: "Cerrar",
    themeLightDescription: "Salida pensada para papel con tarjetas mas claras y menos tinta.",
    themeDarkDescription: "Packet oscuro para revisar en pantalla e imprimir en papel premium.",
    evidenceSelection: "Seleccion de pruebas",
    allEvidenceIncluded: "Se incluiran todos los archivos de prueba.",
    selectedEvidence: (count) => `${count} archivo(s) de prueba seleccionado(s).`,
    deselectAll: "Deseleccionar todo",
    selectAll: "Seleccionar todo",
    footer: "La descarga PDF usa el renderer dedicado en lugar del diseno interactivo.",
    downloadIdle: "Descargar PDF",
    downloadBusy: "Preparando PDF...",
  },
    media: {
      close: "Cerrar",
      imageAlt: (index) => `Imagen de prueba ${index + 1}`,
      openLarge: "Abrir grande",
      signedPreviewUnavailable: "Vista previa firmada no disponible",
    },
    panels: {
      localizedEvidenceLabels: CUSTOM_CASE_EVIDENCE_LABELS.es,
      photoPacket: "Paquete de fotos",
      crimeScenePhotos: "Fotos de la escena",
      imageCount: buildCountLabel("imagen", "imagenes"),
      noCrimeScenePhotos: "Todavia no hay fotos de la escena vinculadas.",
      witnessStatements: "Declaraciones de testigos",
      associatedWitness: "Testigo asociado",
      personOfInterest: "Persona de interes",
      statementCount: buildCountLabel("declaracion", "declaraciones"),
      noAssociatedPortrait: "No hay retrato asociado",
      additionalStatements: "Declaraciones adicionales",
      victimProfile: "Perfil de la victima",
      identitySummary: "Resumen de identidad",
      dossier: "expediente",
      victimPortrait: "Retrato de la victima",
      portraitPendingVictim: "Retrato pendiente. Sube una imagen de la victima para completar este expediente.",
      noStructuredProfileFields: "Todavia no se han generado campos estructurados del perfil.",
      noVictimNarrative:
        "Todavia no se ha generado un relato de la victima. Por ahora el perfil solo contiene campos de identidad estructurados.",
      victimProfiles: "Perfiles de la victima",
      suspectDossiers: "Expedientes de sospechosos",
      suspectCount: buildCountLabel("sospechoso", "sospechosos"),
      openDossier: "Abrir expediente",
      closeDossier: "Cerrar expediente",
      portraitPendingSuspect: "Retrato pendiente. Sube una imagen del sospechoso para completar este expediente.",
      suspectEvidence: "Pruebas contra el sospechoso",
      noSuspectEvidence:
        "Todavia no se han redactado pruebas contra el sospechoso. Anade coartada, presion de las pruebas y detalles de oportunidad en el editor del sospechoso.",
      witnessMentions: "Menciones de testigos",
      witnessMentionCount: buildCountLabel("mencion de testigo", "menciones de testigos"),
      noSuspects: "Todavia no hay sospechosos adjuntos a este borrador.",
      noRoleProvided: "No se proporciono rol",
      motive: "Motivo",
      age: "Edad",
      sex: "Sexo",
      hair: "Cabello",
      eyes: "Ojos",
      height: "Altura",
      build: "Complexion",
      maritalStatus: "Estado civil",
      occupation: "Ocupacion",
      employer: "Empleador",
      alibi: "Coartada",
      evidenceAgainst: "Pruebas en contra",
      evidenceInFavor: "Pruebas a favor",
      opportunity: "Oportunidad",
      behaviorNotes: "Notas de comportamiento",
      noTextChainsAvailable: "No hay cadenas de mensajes disponibles.",
      recoveredTextChain: (index) => `Cadena de mensajes recuperada ${index + 1}`,
      messageChains: "Cadenas de mensajes",
      messageCount: (count) => `${count} msgs`,
      chainLabel: (index) => `Cadena ${index + 1}`,
      recoveredEmail: (index) => `Correo recuperado ${index + 1}`,
      subjectLine: "Asunto",
      universityMailArchive: "Archivo de correo universitario",
      emailThread: "Hilo de correo",
      from: "De",
      to: "Para",
      cc: "CC",
      date: "Fecha",
      recoveredFromArchiveExport: "Recuperado de la exportacion del archivo",
      archivedPress: "Prensa archivada",
      newspaperCoverage: "Cobertura periodistica",
      pressClip: "Recorte de prensa",
      byline: "Firma",
      staffReporter: "Redaccion",
      undatedClipping: "Recorte sin fecha",
      archiveReproduction: "Reproduccion de archivo",
      ottawaPoliceService: "Servicio de Policia de Ottawa",
      majorCrimesDivision: "Division de delitos mayores",
      incidentReport: "Informe del incidente",
      filedDocumentSummary: "Resumen del documento presentado",
      internalUse: "Uso interno",
      fileLabel: (caseNumber) => `Expediente ${caseNumber}`,
      caseNumber: "Numero de caso",
      pendingAssignment: "Asignacion pendiente",
      incidentWindow: "Ventana del incidente",
      seeReportBody: "Ver cuerpo del informe",
      bodyDiscovery: "Hallazgo del cuerpo",
      officeChiefForensicPathologist: "Oficina del patologo forense jefe",
      forensicPathologyUnit: "Unidad de patologia forense",
      postMortemExaminationReport: "Informe de examen post mortem",
      autopsySummary:
        "Resumen forense de lesiones, hallazgos fisiologicos y conclusiones del examinador registradas en la autopsia.",
      medicalExaminerCopy: "Copia del medico forense",
      caseLabel: (caseNumber) => `Caso ${caseNumber}`,
      decedent: "Fallecido",
      notListed: "No indicado",
      caseFile: "Expediente",
      pending: "Pendiente",
      sectionLabel: (index) => `Seccion ${String(index + 1).padStart(2, "0")}`,
      imagePrompt: "Prompt de imagen",
      portraitPrompt: "Prompt de retrato",
    },
    solution: {
    ...CUSTOM_CASE_VIEWER_COPY.en.solution,
    finalRevealEyebrow: "Revelacion final",
    readyTitle: "Lee esto solo cuando estes listo para resolver el caso",
    readyDescription: "Este archivo contiene la respuesta completa, la persona culpable y la cadena de razonamiento. Abrelo solo despues de tu acusacion final.",
    warningEyebrow: "Advertencia",
    warningDescription: "Una vez que abras la solucion, el misterio desaparece. Haz una pausa si tu grupo todavia quiere debatir sospechosos, motivo, medios y oportunidad.",
    arrestWarrant: "Orden de arresto",
    greetingCustom: (actorLabel) => `Felicidades por llegar al final del caso, ${actorLabel}.`,
    greetingMember: (rankLabel, actorLabel) => `Felicidades por llegar al final del caso, ${rankLabel} ${actorLabel}.`,
    arrestPrompt: "Es el momento de pedir una orden de arresto. Selecciona al sospechoso que crees que organizo este crimen.",
    suspectSelection: "Seleccion del sospechoso",
    selectedSuspect: "Sospechoso seleccionado",
    noPhoto: "Sin foto",
    suspectDossier: "Dossier del sospechoso",
    submitting: "Enviando...",
    warrantApproved: "Orden aprobada",
    warrantRequest: "Enviar orden",
    warrantFiled: "Orden presentada",
    previewOnly: "Solo vista previa",
    previewOnlyDescription: "Modo de vista previa. La seleccion de sospechoso se muestra aqui para verificar el contenido.",
    attempts: "Intentos",
    filedSuspect: "Sospechoso presentado",
    unknownSuspect: "Sospechoso desconocido",
    result: "Resultado",
    correctWarrantApproved: "Orden correcta aprobada",
    incorrectSuspectTryAgain: "Sospechoso incorrecto. Intentalo otra vez.",
    correctFirstWarrant: "Primera orden correcta",
    solutionUnlockedWithoutSolveCredit: "Solucion abierta sin credito de resolucion",
    recordSummary: (count, nextRankLabel, solvesToNextRank) =>
      `Registro: ${count} caso${count === 1 ? "" : "s"} resuelto${count === 1 ? "" : "s"}${
        nextRankLabel ? `, ${Number(solvesToNextRank || 0)} hasta ${nextRankLabel}.` : "."
      }`,
    customSolutionUnlocked: "La solucion oficial ya esta abierta para este caso personalizado.",
    customRetryPrompt: "Esa orden no fue correcta. Revisa las pruebas y presenta otra solicitud.",
    guestSessionUnlocked: "Este resultado de invitado abre la solucion solo para esta sesion.",
    solutionAccess: "Acceso a la solucion",
    openOfficialSolution: "Abrir la solucion oficial",
    reveal: "Revelar",
    hide: "Ocultar",
    submitCorrectPrompt: "Presenta la orden correcta arriba para abrir la solucion oficial.",
    submitWarrantPrompt: "Presenta una solicitud de orden arriba para abrir la solucion oficial.",
    shareTheWin: "Compartir la victoria",
    shareDescription: "Publica tu resultado con un toque o copia el mensaje de abajo para redes sociales.",
    shareNow: "Compartir ahora",
    copied: "Copiado",
    copyFailed: "Error al copiar",
    copyShareText: "Copiar texto",
    shareTextCustom: (caseTitle) => `ColdCaseGameKits.com - Acabamos de resolver nuestro caso personalizado "${caseTitle}"`,
    shareTextAgency: (caseTitle) => `Hemos resuelto "${caseTitle}" en Cold Case Agency. ${COLD_CASE_WEBSITE_URL}`,
    nativeShareTitle: (caseTitle) => `Hemos resuelto "${caseTitle}"`,
  },
};

CUSTOM_CASE_VIEWER_COPY.de = {
  ...CUSTOM_CASE_VIEWER_COPY.en,
  pageTitleSuffix: "Zugang zum individuellen Fall",
  customCaseFile: "Individuelle Fallakte",
  defaultTitle: "Dein individueller Fall",
  defaultSummary: "Dieser private Link offnet deinen individuellen Fall, sobald er fertig ist.",
  loadingMessage: "Dein Fallzugang wird geladen...",
  accessProblem: "Zugriffsproblem",
  evidenceFileNotFound: "Beweisdatei nicht gefunden.",
  buildStatus: "Build-Status",
  refresh: "Aktualisieren",
  privateCaseBadge: "Privater Link",
  privateCustomDelivery: "Private individuelle Zustellung",
  sharedCustomDelivery: "Geteilte individuelle Zustellung",
  accessMode: "Zugriffsmodus",
  ownerLink: "Besitzer-Link",
  guestLink: "Gastlink",
  privateCustomAccessChip: "Privater individueller Zugang",
  guestShareAccessChip: "Geteilter Gastzugang",
  evidenceVolume: "Beweisvolumen",
  sections: (count) => `${count} Abschnitt${count === 1 ? "" : "e"}`,
  evidenceRecords: (count) => `${count} Beweisdatensatz${count === 1 ? "" : "e"}`,
  imageAssets: (count) => `${count} Bildressource${count === 1 ? "" : "n"}`,
  detectiveBriefing: "Detektiv-Briefing",
  whatOpensHere: "Was sich hier offnet",
  fullCaseExperience: "Vollstandiges Fallerlebnis",
  privateLinkCard: "Privater Link",
  customCaseEvidence: "Beweisdatei des individuellen Falls",
  backToCaseFile: "Zuruck zur Fallakte",
  openPdfPacket: "PDF offnen",
  createPdf: "PDF erstellen",
  metadata: {
    date: "Datum",
    source: "Quelle",
    confidentiality: "Vertraulichkeit",
    evidenceType: "Beweistyp",
    noDate: "Kein Datum",
    noSource: "Keine Quelle",
    internal: "intern",
  },
  pager: {
    previousFile: "Vorherige Datei",
    nextFile: "Nachste Datei",
    previousEvidence: "Vorheriger Beweis",
    nextEvidence: "Nachster Beweis",
  },
  warrant: {
    detectiveLabel: "Detektiv",
    detectiveRank: "Detektiv fur individuelle Falle",
    approved: "Haftbefehl genehmigt. Deine Auswahl war richtig und die komplette Losung ist jetzt freigeschaltet.",
    rejected: "Diese verdachtige Person ist nicht korrekt. Prufe die Beweise und reiche einen neuen Haftbefehl ein.",
    reset: "Haftbefehlstatus fur lokale Tests zuruckgesetzt.",
  },
  share: {
    ...CUSTOM_CASE_VIEWER_COPY.en.share,
    sectionEyebrow: "Gastfreigabe",
    sectionTitle: "Separaten Gastlink erstellen",
    sectionBody: "Teile einen Gastlink statt deiner Besitzer-URL. Gastlinks laufen nach 24 Stunden ab, konnen widerrufen werden und zeigen weder Intake noch Besitzerzugang.",
    expiresIn24Hours: "Gastlinks laufen nach 24 Stunden ab.",
    createIdle: "Gastlink erstellen",
    createBusy: "Gastlink wird erstellt...",
    newestLink: "Neuester Gastlink",
    copyLink: "Gastlink kopieren",
    copied: "Gastlink kopiert.",
    created: "Gastlink erstellt. Teile diesen Link statt deiner Besitzer-URL.",
    revoked: "Gastlink widerrufen.",
    revoke: "Gastlink widerrufen",
    noActiveLinks: "Noch keine aktiven Gastlinks.",
    guestLinkFallback: "Gastlink",
    expiresPrefix: "Lauft ab",
    expiresSoon: "bald",
    accessCount: (count) => `${count} Zugriff${count === 1 ? "" : "e"}`,
  },
  errors: {
    loadCase: "Dein individueller Fall konnte nicht geladen werden.",
    createGuestLink: "Gastlink konnte nicht erstellt werden.",
    copyGuestLink: "Gastlink konnte nicht kopiert werden.",
    revokeGuestLink: "Gastlink konnte nicht widerrufen werden.",
    submitWarrant: "Haftbefehl konnte nicht eingereicht werden.",
    downloadPdf: "Fall-PDF konnte nicht heruntergeladen werden.",
    resetWarrant: "Haftbefehlstatus konnte nicht zuruckgesetzt werden.",
  },
  video: {
    eyebrow: "Individueller Fall",
    title: "Begrussungsvideo",
    open: "Begrussungsvideo offnen",
    empty: "Diesem Fall ist noch kein Briefing-Video zugeordnet.",
    iframeTitle: "Briefing-Video",
  },
  briefing: {
    eyebrow: "Einsatznotizen",
    title: "Briefing-Notizen",
    buildStatusLink: "Build-Status",
    defaultBody: "Lies das Briefing, untersuche jede Datei und entwickle deine Theorie, bevor du die wahrscheinlichste verdachtige Person benennst.",
    setting: "Schauplatz",
    unknownPlace: "Unbekannter Ort",
    unknownTime: "Unbekannte Zeit",
    delivery: "Zustellung",
    deliveryValue: "ZUGANG ZUM INDIVIDUELLEN FALL",
    deliveryBody: "Dieser private Link erfordert keine Agency-Mitgliedschaft.",
  },
  archive: {
    eyebrow: "Feldarchiv",
    title: "Beweisindex",
    accessLabel: "Individueller Zugang",
    openFile: "Datei offnen",
    includedSuspectDossiers: "Enthaltene Verdachtigen-Dossiers",
  },
  suspectDossiers: {
    eyebrow: "Verdachtigen-Dossiers",
    title: "Alle in der Akte",
    count: (count) => `${count} Verdachtige${count === 1 ? "r" : ""}`,
    fallbackName: (index) => `Verdachtige Person ${index + 1}`,
  },
  ownerGate: {
    title: "Privaten Link verifizieren",
    description: "Dieser Besitzer-Link offnet die private Version deines individuellen Falls. Wir senden dir einen einmaligen E-Mail-Code, bevor wir ihn in diesem Browser offnen.",
    copy: {
      loading: "Privater Zugriff wird gepruft...",
      badge: "Privater Besitzerzugang",
      sendCodeToOrderEmail: "Code an die Bestell-E-Mail senden.",
      rememberedNotice: "Wir fragen das nur in einem neuen Browser oder auf einem neuen Gerat ab. Nach der Verifizierung bleibt dieser Browser gespeichert.",
      requestIdle: "Zugangscode per E-Mail senden",
      requestBusy: "Code wird gesendet...",
      codeLabel: "6-stelliger Code",
      codePlaceholder: "123456",
      verifyIdle: "Verifizieren und fortfahren",
      verifyBusy: "Verifizierung...",
      clearRememberedAccess: "Gespeicherten Zugriff loschen",
      devCodePreview: "Dev-Code",
      invalidCode: "Gib den 6-stelligen Code aus deiner E-Mail ein.",
      statusCodeSent: "Zugangscode gesendet. Gib ihn unten ein, um fortzufahren.",
      statusVerified: "Zugriff fur diesen Browser bestatigt.",
      statusCleared: "Gespeicherter Zugriff fur diesen Browser geloscht.",
      loadAccessStateError: "Zugriffsstatus konnte nicht geladen werden.",
      requestCodeError: "Zugangscode konnte nicht gesendet werden.",
      verifyCodeError: "Zugangscode konnte nicht verifiziert werden.",
      clearAccessError: "Gespeicherter Zugriff konnte nicht geloscht werden.",
    },
  },
  printModal: {
    ...CUSTOM_CASE_VIEWER_COPY.en.printModal,
    eyebrow: "PDF herunterladen",
    title: "Fallakte vorbereiten",
    description: "Wahle das Papier-Thema und die Beweise aus und lade dann das servergerenderte PDF herunter.",
    close: "Schliessen",
    themeLightDescription: "Papierorientierte Ausgabe mit helleren Karten und weniger Tintenverbrauch.",
    themeDarkDescription: "Dunkles Paket fur die Bildschirmprufung und Premiumdruck.",
    evidenceSelection: "Beweisauswahl",
    allEvidenceIncluded: "Alle Beweisdateien werden aufgenommen.",
    selectedEvidence: (count) => `${count} Beweisdatei(en) ausgewahlt.`,
    deselectAll: "Alles abwahlen",
    selectAll: "Alles auswahlen",
    footer: "Der PDF-Download nutzt den speziellen Druck-Renderer statt des interaktiven Layouts.",
    downloadIdle: "PDF herunterladen",
    downloadBusy: "PDF wird vorbereitet...",
  },
    media: {
      close: "Schliessen",
      imageAlt: (index) => `Beweisbild ${index + 1}`,
      openLarge: "Gross offnen",
      signedPreviewUnavailable: "Signierte Vorschau nicht verfugbar",
    },
    panels: {
      localizedEvidenceLabels: CUSTOM_CASE_EVIDENCE_LABELS.de,
      photoPacket: "Fotopaket",
      crimeScenePhotos: "Tatortfotos",
      imageCount: buildCountLabel("Bild", "Bilder"),
      noCrimeScenePhotos: "Es sind noch keine Tatortfotos verknupft.",
      witnessStatements: "Zeugenaussagen",
      associatedWitness: "Zugeordneter Zeuge",
      personOfInterest: "Interessante Person",
      statementCount: buildCountLabel("Aussage", "Aussagen"),
      noAssociatedPortrait: "Kein zugeordnetes Portrat",
      additionalStatements: "Weitere Aussagen",
      victimProfile: "Opferprofil",
      identitySummary: "Identitatsubersicht",
      dossier: "Dossier",
      victimPortrait: "Opferportrat",
      portraitPendingVictim: "Portrat ausstehend. Lade ein Opferbild hoch, um dieses Dossier zu vervollstandigen.",
      noStructuredProfileFields: "Es wurden noch keine strukturierten Profilfelder erzeugt.",
      noVictimNarrative:
        "Es wurde noch kein Opferbericht erzeugt. Das Profil enthalt derzeit nur strukturierte Identitatsfelder.",
      victimProfiles: "Opferprofile",
      suspectDossiers: "Verdachtigen-Dossiers",
      suspectCount: buildCountLabel("Verdachtiger", "Verdachtige"),
      openDossier: "Dossier offnen",
      closeDossier: "Dossier schliessen",
      portraitPendingSuspect: "Portrat ausstehend. Lade ein Verdachtigenbild hoch, um dieses Dossier zu vervollstandigen.",
      suspectEvidence: "Beweise gegen den Verdachtigen",
      noSuspectEvidence:
        "Es wurden noch keine Beweise gegen den Verdachtigen verfasst. Fuge Alibi, Beweisdruck und Gelegenheitsdetails im Verdachtigen-Editor hinzu.",
      witnessMentions: "Zeugenhinweise",
      witnessMentionCount: buildCountLabel("Zeugenhinweis", "Zeugenhinweise"),
      noSuspects: "Diesem Entwurf sind noch keine Verdachtigen zugeordnet.",
      noRoleProvided: "Keine Rolle angegeben",
      motive: "Motiv",
      age: "Alter",
      sex: "Geschlecht",
      hair: "Haare",
      eyes: "Augen",
      height: "Grosse",
      build: "Statur",
      maritalStatus: "Familienstand",
      occupation: "Beruf",
      employer: "Arbeitgeber",
      alibi: "Alibi",
      evidenceAgainst: "Belastende Beweise",
      evidenceInFavor: "Entlastende Beweise",
      opportunity: "Gelegenheit",
      behaviorNotes: "Verhaltensnotizen",
      noTextChainsAvailable: "Keine Nachrichtenketten verfugbar.",
      recoveredTextChain: (index) => `Wiederhergestellte Nachrichtenkette ${index + 1}`,
      messageChains: "Nachrichtenketten",
      messageCount: (count) => `${count} Msgs`,
      chainLabel: (index) => `Kette ${index + 1}`,
      recoveredEmail: (index) => `Wiederhergestellte E-Mail ${index + 1}`,
      subjectLine: "Betreff",
      universityMailArchive: "Universitats-Mailarchiv",
      emailThread: "E-Mail-Verlauf",
      from: "Von",
      to: "An",
      cc: "CC",
      date: "Datum",
      recoveredFromArchiveExport: "Aus dem Archivexport wiederhergestellt",
      archivedPress: "Archivierte Presse",
      newspaperCoverage: "Zeitungsberichterstattung",
      pressClip: "Presseausschnitt",
      byline: "Verfasser",
      staffReporter: "Redaktion",
      undatedClipping: "Undatierter Ausschnitt",
      archiveReproduction: "Archivreproduktion",
      ottawaPoliceService: "Polizeidienst Ottawa",
      majorCrimesDivision: "Abteilung fur schwere Straftaten",
      incidentReport: "Einsatzbericht",
      filedDocumentSummary: "Zusammenfassung des eingereichten Dokuments",
      internalUse: "Nur fur den internen Gebrauch",
      fileLabel: (caseNumber) => `Akte ${caseNumber}`,
      caseNumber: "Fallnummer",
      pendingAssignment: "Zuweisung ausstehend",
      incidentWindow: "Tatzeitfenster",
      seeReportBody: "Siehe Berichtstext",
      bodyDiscovery: "Auffinden des Korpers",
      officeChiefForensicPathologist: "Amt des leitenden forensischen Pathologen",
      forensicPathologyUnit: "Einheit fur forensische Pathologie",
      postMortemExaminationReport: "Bericht zur postmortalen Untersuchung",
      autopsySummary:
        "Forensische Zusammenfassung der Verletzungen, physiologischen Befunde und Schlussfolgerungen des Untersuchers aus der Autopsie.",
      medicalExaminerCopy: "Exemplar fur die Gerichtsmedizin",
      caseLabel: (caseNumber) => `Fall ${caseNumber}`,
      decedent: "Verstorbene Person",
      notListed: "Nicht angegeben",
      caseFile: "Fallakte",
      pending: "Ausstehend",
      sectionLabel: (index) => `Abschnitt ${String(index + 1).padStart(2, "0")}`,
      imagePrompt: "Bildprompt",
      portraitPrompt: "Portraet-Prompt",
    },
    solution: {
    ...CUSTOM_CASE_VIEWER_COPY.en.solution,
    finalRevealEyebrow: "Finale Enthullung",
    readyTitle: "Lies das nur, wenn du bereit bist, den Fall zu losen",
    readyDescription: "Diese Datei enthalt die vollstandige Antwort, die schuldige Person und die Beweiskette. Offne sie erst nach deiner letzten Anschuldigung.",
    warningEyebrow: "Warnung",
    warningDescription: "Sobald du die Losung offnest, ist das Ratsel vorbei. Pause hier, wenn deine Gruppe noch uber Verdachtige, Motiv, Mittel und Gelegenheit sprechen will.",
    arrestWarrant: "Haftbefehl",
    greetingCustom: (actorLabel) => `Gluckwunsch zum Erreichen des Fallendes, ${actorLabel}.`,
    greetingMember: (rankLabel, actorLabel) => `Gluckwunsch zum Erreichen des Fallendes, ${rankLabel} ${actorLabel}.`,
    arrestPrompt: "Jetzt ist der Moment fur einen Haftbefehl. Wahle die verdachtige Person, die dieses Verbrechen geplant hat.",
    suspectSelection: "Verdachtige Person",
    selectedSuspect: "Ausgewahlte verdachtige Person",
    noPhoto: "Kein Foto",
    suspectDossier: "Verdachtigen-Dossier",
    submitting: "Wird gesendet...",
    warrantApproved: "Haftbefehl genehmigt",
    warrantRequest: "Haftbefehl anfordern",
    warrantFiled: "Haftbefehl eingereicht",
    previewOnly: "Nur Vorschau",
    previewOnlyDescription: "Nur Vorschau. Die Verdachtigtenauswahl wird hier zur Inhaltsprufung gezeigt.",
    attempts: "Versuche",
    filedSuspect: "Eingereichte verdachtige Person",
    unknownSuspect: "Unbekannte verdachtige Person",
    result: "Ergebnis",
    correctWarrantApproved: "Korrekter Haftbefehl genehmigt",
    incorrectSuspectTryAgain: "Falsche verdachtige Person. Versuch es erneut.",
    correctFirstWarrant: "Erster Haftbefehl korrekt",
    solutionUnlockedWithoutSolveCredit: "Losung ohne Losungs-Credit freigeschaltet",
    recordSummary: (count, nextRankLabel, solvesToNextRank) =>
      `Akte: ${count} erfolgreicher Abschluss${count === 1 ? "" : "e"}${
        nextRankLabel ? `, noch ${Number(solvesToNextRank || 0)} bis ${nextRankLabel}.` : "."
      }`,
    customSolutionUnlocked: "Die offizielle Losung ist jetzt fur diesen individuellen Fall freigeschaltet.",
    customRetryPrompt: "Dieser Haftbefehl war nicht korrekt. Prufe die Beweise und reiche einen weiteren Antrag ein.",
    guestSessionUnlocked: "Dieses Gastergebnis schaltet die Losung nur fur diese Sitzung frei.",
    solutionAccess: "Losungszugang",
    openOfficialSolution: "Offizielle Falllosung offnen",
    reveal: "Zeigen",
    hide: "Ausblenden",
    submitCorrectPrompt: "Reiche oben den richtigen Haftbefehl ein, um die offizielle Losung freizuschalten.",
    submitWarrantPrompt: "Reiche oben einen Haftbefehl ein, um die offizielle Losung freizuschalten.",
    shareTheWin: "Erfolg teilen",
    shareDescription: "Teile dein Ergebnis mit einem Klick oder kopiere die Nachricht unten fur soziale Medien.",
    shareNow: "Jetzt teilen",
    copied: "Kopiert",
    copyFailed: "Kopieren fehlgeschlagen",
    copyShareText: "Text kopieren",
    shareTextCustom: (caseTitle) => `ColdCaseGameKits.com - Wir haben unseren individuellen Fall "${caseTitle}" gelost`,
    shareTextAgency: (caseTitle) => `Wir haben "${caseTitle}" bei der Cold Case Agency gelost. ${COLD_CASE_WEBSITE_URL}`,
    nativeShareTitle: (caseTitle) => `Wir haben "${caseTitle}" gelost`,
  },
};

CUSTOM_CASE_VIEWER_COPY.it = {
  ...CUSTOM_CASE_VIEWER_COPY.en,
  pageTitleSuffix: "Accesso al caso personalizzato",
  customCaseFile: "Fascicolo personalizzato",
  defaultTitle: "Il tuo caso personalizzato",
  defaultSummary: "Questo link privato apre il tuo caso personalizzato quando sara pronto.",
  loadingMessage: "Caricamento dell'accesso al caso...",
  accessProblem: "Problema di accesso",
  evidenceFileNotFound: "File di prova non trovato.",
  buildStatus: "Stato build",
  refresh: "Aggiorna",
  privateCaseBadge: "Link privato",
  privateCustomDelivery: "Consegna privata personalizzata",
  sharedCustomDelivery: "Consegna condivisa personalizzata",
  accessMode: "Modalita di accesso",
  ownerLink: "Link proprietario",
  guestLink: "Link ospite",
  privateCustomAccessChip: "Accesso privato personalizzato",
  guestShareAccessChip: "Accesso condiviso ospite",
  evidenceVolume: "Volume prove",
  sections: (count) => `${count} sezione${count === 1 ? "" : "i"}`,
  evidenceRecords: (count) => `${count} record${count === 1 ? " prova" : " prove"}`,
  imageAssets: (count) => `${count} risors${count === 1 ? "a immagine" : "e immagine"}`,
  detectiveBriefing: "Briefing del detective",
  whatOpensHere: "Cosa si apre qui",
  fullCaseExperience: "Esperienza completa",
  privateLinkCard: "Link privato",
  customCaseEvidence: "Prova del caso personalizzato",
  backToCaseFile: "Torna al fascicolo",
  openPdfPacket: "Apri PDF",
  createPdf: "Crea PDF",
  metadata: {
    date: "Data",
    source: "Fonte",
    confidentiality: "Riservatezza",
    evidenceType: "Tipo di prova",
    noDate: "Nessuna data",
    noSource: "Nessuna fonte",
    internal: "interno",
  },
  pager: {
    previousFile: "File precedente",
    nextFile: "File successivo",
    previousEvidence: "Prova precedente",
    nextEvidence: "Prova successiva",
  },
  warrant: {
    detectiveLabel: "Detective",
    detectiveRank: "Detective del caso personalizzato",
    approved: "Mandato approvato. La tua scelta era corretta e la soluzione completa ora e sbloccata.",
    rejected: "Questo sospetto non e corretto. Rivedi le prove e presenta un nuovo mandato.",
    reset: "Stato del mandato reimpostato per i test locali.",
  },
  share: {
    ...CUSTOM_CASE_VIEWER_COPY.en.share,
    sectionEyebrow: "Condivisione ospiti",
    sectionTitle: "Crea un link ospite separato",
    sectionBody: "Condividi un link ospite invece del tuo URL proprietario. I link ospite scadono dopo 24 ore, possono essere revocati e non espongono il tuo intake.",
    expiresIn24Hours: "I link ospite scadono dopo 24 ore.",
    createIdle: "Crea link ospite",
    createBusy: "Creazione link ospite...",
    newestLink: "Link ospite piu recente",
    copyLink: "Copia link ospite",
    copied: "Link ospite copiato.",
    created: "Link ospite creato. Condividi questo link invece del tuo URL proprietario.",
    revoked: "Link ospite revocato.",
    revoke: "Revoca link ospite",
    noActiveLinks: "Nessun link ospite attivo per ora.",
    guestLinkFallback: "Link ospite",
    expiresPrefix: "Scade",
    expiresSoon: "presto",
    accessCount: (count) => `${count} access${count === 1 ? "o" : "i"}`,
  },
  errors: {
    loadCase: "Impossibile caricare il tuo caso personalizzato.",
    createGuestLink: "Impossibile creare il link ospite.",
    copyGuestLink: "Impossibile copiare il link ospite.",
    revokeGuestLink: "Impossibile revocare il link ospite.",
    submitWarrant: "Impossibile inviare il mandato.",
    downloadPdf: "Impossibile scaricare il PDF del caso.",
    resetWarrant: "Impossibile reimpostare lo stato del mandato.",
  },
  video: {
    eyebrow: "Caso personalizzato",
    title: "Video di benvenuto",
    open: "Apri video di benvenuto",
    empty: "A questo caso non e ancora associato alcun video di briefing.",
    iframeTitle: "Video briefing",
  },
  briefing: {
    eyebrow: "Note di assegnazione",
    title: "Note di briefing",
    buildStatusLink: "Stato build",
    defaultBody: "Rivedi il briefing, esamina ogni file e costruisci la tua teoria prima di indicare il sospetto piu probabile.",
    setting: "Ambientazione",
    unknownPlace: "Luogo sconosciuto",
    unknownTime: "Ora sconosciuta",
    delivery: "Consegna",
    deliveryValue: "ACCESSO CASO PERSONALIZZATO",
    deliveryBody: "Questo link privato non richiede un abbonamento Agency.",
  },
  archive: {
    eyebrow: "Archivio sul campo",
    title: "Indice delle prove",
    accessLabel: "Accesso personalizzato",
    openFile: "Apri file",
    includedSuspectDossiers: "Dossier sospetti inclusi",
  },
  suspectDossiers: {
    eyebrow: "Dossier dei sospetti",
    title: "Tutti nel fascicolo",
    count: (count) => `${count} sospett${count === 1 ? "o" : "i"}`,
    fallbackName: (index) => `Sospetto ${index + 1}`,
  },
  ownerGate: {
    title: "Verifica il tuo link privato",
    description: "Questo link proprietario apre la versione privata del tuo caso personalizzato. Ti invieremo un codice email monouso prima di aprirlo su questo browser.",
    copy: {
      loading: "Verifica dell'accesso privato...",
      badge: "Accesso proprietario privato",
      sendCodeToOrderEmail: "Invia il codice all'email dell'ordine.",
      rememberedNotice: "Lo chiediamo solo su un nuovo browser o dispositivo. Dopo la verifica, questo browser restera ricordato.",
      requestIdle: "Invia codice di accesso",
      requestBusy: "Invio del codice...",
      codeLabel: "Codice a 6 cifre",
      codePlaceholder: "123456",
      verifyIdle: "Verifica e continua",
      verifyBusy: "Verifica in corso...",
      clearRememberedAccess: "Cancella accesso ricordato",
      devCodePreview: "Codice dev",
      invalidCode: "Inserisci il codice a 6 cifre ricevuto via email.",
      statusCodeSent: "Codice inviato. Inseriscilo qui sotto per continuare.",
      statusVerified: "Accesso confermato per questo browser.",
      statusCleared: "Accesso ricordato cancellato per questo browser.",
      loadAccessStateError: "Impossibile caricare lo stato di accesso.",
      requestCodeError: "Impossibile inviare il codice di accesso.",
      verifyCodeError: "Impossibile verificare il codice di accesso.",
      clearAccessError: "Impossibile cancellare l'accesso ricordato.",
    },
  },
  printModal: {
    ...CUSTOM_CASE_VIEWER_COPY.en.printModal,
    eyebrow: "Scarica PDF",
    title: "Prepara il fascicolo",
    description: "Scegli il tema carta e le prove, poi scarica il PDF generato dal server.",
    close: "Chiudi",
    themeLightDescription: "Output pensato per la carta con schede piu chiare e meno inchiostro.",
    themeDarkDescription: "Pacchetto scuro per revisione a schermo e stampa premium.",
    evidenceSelection: "Selezione prove",
    allEvidenceIncluded: "Tutti i file di prova saranno inclusi.",
    selectedEvidence: (count) => `${count} file di prova selezionato/i.`,
    deselectAll: "Deseleziona tutto",
    selectAll: "Seleziona tutto",
    footer: "Il download PDF usa il renderer di stampa dedicato invece del layout interattivo.",
    downloadIdle: "Scarica PDF",
    downloadBusy: "Preparazione PDF...",
  },
    media: {
      close: "Chiudi",
      imageAlt: (index) => `Immagine di prova ${index + 1}`,
      openLarge: "Apri grande",
      signedPreviewUnavailable: "Anteprima firmata non disponibile",
    },
    panels: {
      localizedEvidenceLabels: CUSTOM_CASE_EVIDENCE_LABELS.it,
      photoPacket: "Pacchetto foto",
      crimeScenePhotos: "Foto della scena",
      imageCount: buildCountLabel("immagine", "immagini"),
      noCrimeScenePhotos: "Non ci sono ancora foto della scena collegate.",
      witnessStatements: "Dichiarazioni dei testimoni",
      associatedWitness: "Testimone associato",
      personOfInterest: "Persona di interesse",
      statementCount: buildCountLabel("dichiarazione", "dichiarazioni"),
      noAssociatedPortrait: "Nessun ritratto associato",
      additionalStatements: "Dichiarazioni aggiuntive",
      victimProfile: "Profilo della vittima",
      identitySummary: "Riepilogo dell'identita",
      dossier: "dossier",
      victimPortrait: "Ritratto della vittima",
      portraitPendingVictim: "Ritratto in attesa. Carica un'immagine della vittima per completare questo dossier.",
      noStructuredProfileFields: "Non sono ancora stati generati campi profilo strutturati.",
      noVictimNarrative:
        "Non e ancora stato generato un racconto della vittima. Per ora il profilo contiene solo campi di identita strutturati.",
      victimProfiles: "Profili della vittima",
      suspectDossiers: "Dossier dei sospetti",
      suspectCount: buildCountLabel("sospetto", "sospetti"),
      openDossier: "Apri dossier",
      closeDossier: "Chiudi dossier",
      portraitPendingSuspect: "Ritratto in attesa. Carica un'immagine del sospetto per completare questo dossier.",
      suspectEvidence: "Prove contro il sospetto",
      noSuspectEvidence:
        "Non sono ancora state scritte prove contro il sospetto. Aggiungi alibi, pressione delle prove e dettagli di opportunita nell'editor del sospetto.",
      witnessMentions: "Menzioni dei testimoni",
      witnessMentionCount: buildCountLabel("menzione di testimone", "menzioni dei testimoni"),
      noSuspects: "Non ci sono ancora sospetti collegati a questa bozza.",
      noRoleProvided: "Nessun ruolo fornito",
      motive: "Movente",
      age: "Eta",
      sex: "Sesso",
      hair: "Capelli",
      eyes: "Occhi",
      height: "Altezza",
      build: "Corporatura",
      maritalStatus: "Stato civile",
      occupation: "Occupazione",
      employer: "Datore di lavoro",
      alibi: "Alibi",
      evidenceAgainst: "Prove contro",
      evidenceInFavor: "Prove a favore",
      opportunity: "Opportunita",
      behaviorNotes: "Note comportamentali",
      noTextChainsAvailable: "Nessuna catena di messaggi disponibile.",
      recoveredTextChain: (index) => `Catena di messaggi recuperata ${index + 1}`,
      messageChains: "Catene di messaggi",
      messageCount: (count) => `${count} msg`,
      chainLabel: (index) => `Catena ${index + 1}`,
      recoveredEmail: (index) => `Email recuperata ${index + 1}`,
      subjectLine: "Oggetto",
      universityMailArchive: "Archivio email universitario",
      emailThread: "Thread email",
      from: "Da",
      to: "A",
      cc: "CC",
      date: "Data",
      recoveredFromArchiveExport: "Recuperato dall'esportazione dell'archivio",
      archivedPress: "Stampa d'archivio",
      newspaperCoverage: "Copertura giornalistica",
      pressClip: "Ritaglio stampa",
      byline: "Firma",
      staffReporter: "Redazione",
      undatedClipping: "Ritaglio senza data",
      archiveReproduction: "Riproduzione d'archivio",
      ottawaPoliceService: "Servizio di polizia di Ottawa",
      majorCrimesDivision: "Divisione crimini maggiori",
      incidentReport: "Rapporto d'incidente",
      filedDocumentSummary: "Riepilogo del documento depositato",
      internalUse: "Uso interno",
      fileLabel: (caseNumber) => `Fascicolo ${caseNumber}`,
      caseNumber: "Numero del caso",
      pendingAssignment: "Assegnazione in attesa",
      incidentWindow: "Finestra dell'incidente",
      seeReportBody: "Vedi corpo del rapporto",
      bodyDiscovery: "Ritrovamento del corpo",
      officeChiefForensicPathologist: "Ufficio del capo patologo forense",
      forensicPathologyUnit: "Unita di patologia forense",
      postMortemExaminationReport: "Rapporto di esame post-mortem",
      autopsySummary:
        "Riepilogo forense delle lesioni, dei riscontri fisiologici e delle conclusioni dell'esaminatore registrate durante l'autopsia.",
      medicalExaminerCopy: "Copia del medico legale",
      caseLabel: (caseNumber) => `Caso ${caseNumber}`,
      decedent: "Deceduto",
      notListed: "Non indicato",
      caseFile: "Fascicolo",
      pending: "In attesa",
      sectionLabel: (index) => `Sezione ${String(index + 1).padStart(2, "0")}`,
      imagePrompt: "Prompt immagine",
      portraitPrompt: "Prompt ritratto",
    },
    solution: {
    ...CUSTOM_CASE_VIEWER_COPY.en.solution,
    finalRevealEyebrow: "Rivelazione finale",
    readyTitle: "Leggi questo solo quando sei pronto a risolvere il caso",
    readyDescription: "Questo file contiene la risposta completa, la persona colpevole e la catena logica. Aprilo solo dopo la tua accusa finale.",
    warningEyebrow: "Avviso",
    warningDescription: "Una volta aperta la soluzione, il mistero sparisce. Fermati qui se il tuo gruppo vuole ancora discutere sospetti, movente, mezzi e opportunita.",
    arrestWarrant: "Mandato di arresto",
    greetingCustom: (actorLabel) => `Congratulazioni per essere arrivato alla fine del caso, ${actorLabel}.`,
    greetingMember: (rankLabel, actorLabel) => `Congratulazioni per essere arrivato alla fine del caso, ${rankLabel} ${actorLabel}.`,
    arrestPrompt: "Questo e il momento di richiedere un mandato. Seleziona il sospetto che credi abbia orchestrato il crimine.",
    suspectSelection: "Selezione del sospetto",
    selectedSuspect: "Sospetto selezionato",
    noPhoto: "Nessuna foto",
    suspectDossier: "Dossier del sospetto",
    submitting: "Invio in corso...",
    warrantApproved: "Mandato approvato",
    warrantRequest: "Invia richiesta di mandato",
    warrantFiled: "Mandato depositato",
    previewOnly: "Solo anteprima",
    previewOnlyDescription: "Modalita anteprima. La selezione del sospetto viene mostrata qui per verificare il contenuto.",
    attempts: "Tentativi",
    filedSuspect: "Sospetto depositato",
    unknownSuspect: "Sospetto sconosciuto",
    result: "Risultato",
    correctWarrantApproved: "Mandato corretto approvato",
    incorrectSuspectTryAgain: "Sospetto errato. Riprova.",
    correctFirstWarrant: "Primo mandato corretto",
    solutionUnlockedWithoutSolveCredit: "Soluzione sbloccata senza credito di risoluzione",
    recordSummary: (count, nextRankLabel, solvesToNextRank) =>
      `Registro: ${count} soluzione${count === 1 ? "" : "i"} riuscita${count === 1 ? "" : "e"}${
        nextRankLabel ? `, ${Number(solvesToNextRank || 0)} fino a ${nextRankLabel}.` : "."
      }`,
    customSolutionUnlocked: "La soluzione ufficiale e ora sbloccata per questo caso personalizzato.",
    customRetryPrompt: "Quel mandato non era corretto. Rivedi le prove e invia un'altra richiesta.",
    guestSessionUnlocked: "Questo risultato ospite sblocca la soluzione solo per questa sessione.",
    solutionAccess: "Accesso alla soluzione",
    openOfficialSolution: "Apri la soluzione ufficiale",
    reveal: "Rivela",
    hide: "Nascondi",
    submitCorrectPrompt: "Invia il mandato corretto qui sopra per sbloccare la soluzione ufficiale.",
    submitWarrantPrompt: "Invia una richiesta di mandato qui sopra per sbloccare la soluzione ufficiale.",
    shareTheWin: "Condividi la vittoria",
    shareDescription: "Pubblica il risultato con un tocco o copia il messaggio qui sotto per i social.",
    shareNow: "Condividi ora",
    copied: "Copiato",
    copyFailed: "Copia non riuscita",
    copyShareText: "Copia testo",
    shareTextCustom: (caseTitle) => `ColdCaseGameKits.com - Abbiamo appena risolto il nostro caso personalizzato "${caseTitle}"`,
    shareTextAgency: (caseTitle) => `Abbiamo risolto "${caseTitle}" nella Cold Case Agency. ${COLD_CASE_WEBSITE_URL}`,
    nativeShareTitle: (caseTitle) => `Abbiamo risolto "${caseTitle}"`,
  },
};

export const getCustomCaseViewerCopy = (lang: CustomCaseViewerLang) =>
  CUSTOM_CASE_VIEWER_COPY[lang] || CUSTOM_CASE_VIEWER_COPY.en;
